/* eslint-disable @typescript-eslint/ban-ts-comment */
// import { Play, Settings } from "lucide-react"
import { DatePickerWithRange } from "@/components/date-range";
import { createFileRoute, useParams, useSearch } from "@tanstack/react-router";
import { subDays } from "date-fns";
import {
  Download,
  Loader2,
  Settings,
  Mail as MailIcon,
  Network,
  Calendar,
  Hash,
  Copy,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import { useMemo, useRef, useState, useEffect, useCallback } from "react";
import { DateRange } from "react-day-picker";

// import { Button } from '@/components/ui/button'
// import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import DataTable from "@/components/data-table";
import ScheduleTemplates from "@/components/Mailboxes/schedule-templates";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SettingsDialog } from "@/components/ui/settingDialog";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  useDownloadFailedHeaderssenders,
  useExportSenderInteractions,
  useEnableWarmupWithSettings,
  useGetB2BWarmupSettings,
  useMailboxDailyStats,
  useMailboxesbyId,
  useSenderEspTypes,
  useSenderMailboxStats,
  useUpdateMailbox,
  useUpdateMailboxWarmup,
} from "@/hooks/use-mailbox";
import { usePermission } from "@/hooks/use-premission";
import AssignSubscriptionSelect from "@/pages/mailbox/assign-subscripiton-select";
import MailboxDetailLoader from "@/pages/mailbox/mailbox-detail-loader";
import { Plugs, PlugsConnected } from "@phosphor-icons/react";
import { ColDef, ValueGetterParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import {
  FileText,
  Globe,
  HelpCircle,
  Mail,
  MailCheck,
  Users2,
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Info,
  ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import { useManageSeedEspTypes } from "@/hooks/use-b2c-sender";
import { cn, extractErrorMessage } from "@/lib/utils";
import StatusScreen from "@/components/status-screen";
import { authProviderIcons } from "@/pages/mailbox/esp-providers";
import type { B2BWarmupSettings, CustomWarmupPlan } from "@/types/mailbox.types";
import { SenderMailboxStats } from "@/types/mailbox.types";
import { WarmupB2BSettingsDialog } from "@/components/popups/warmup-b2b-settings-dialog";
import { buildTooltipText, getStrategyConfig } from "@/pages/mailbox/warmup-strategy-cell";
import { Clock, PieChart } from "lucide-react";
import { parseRawEmail, ParsedEmail } from "@/utils/email-parser";

// Helper function to format date as YYYY-MM-DD in local timezone (not UTC)
const formatDateLocal = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/** ESP distribution: keys from mailbox.warmupConfig.espBreakdown (API) */
const ESP_BREAKDOWN_KEYS: { key: string; label: string; iconKey: string }[] = [
  { key: "googleWorkspace", label: "Google Workspace", iconKey: "google_workspace" },
  { key: "microsoft365", label: "Microsoft 365", iconKey: "microsoft_365" },
  { key: "gmail", label: "Gmail", iconKey: "gmail" },
  { key: "outlook", label: "Outlook", iconKey: "outlook" },
  { key: "yahoo", label: "Yahoo", iconKey: "yahoo" },
  { key: "zoho", label: "Zoho", iconKey: "zoho" },
];

/** Fallback: keys from b2bSettings (percent fields) */
const ESP_DISTRIBUTION_KEYS: { key: string; label: string; iconKey: string }[] = [
  { key: "googleWorkspacePercent", label: "Google Workspace", iconKey: "google_workspace" },
  { key: "microsoft365Percent", label: "Microsoft 365", iconKey: "microsoft_365" },
  { key: "gmailPercent", label: "Gmail", iconKey: "gmail" },
  { key: "outlookPercent", label: "Outlook", iconKey: "outlook" },
  { key: "yahooPercent", label: "Yahoo", iconKey: "yahoo" },
  { key: "zohoPercent", label: "Zoho", iconKey: "zoho" },
];

/** Display order Mon–Sun for sendOnDays (0=Sun, 1=Mon, … 6=Sat) */
const TIMEZONE_DAY_ORDER: { dayNum: number; letter: string; name: string }[] = [
  { dayNum: 1, letter: "M", name: "Monday" },
  { dayNum: 2, letter: "T", name: "Tuesday" },
  { dayNum: 3, letter: "W", name: "Wednesday" },
  { dayNum: 4, letter: "T", name: "Thursday" },
  { dayNum: 5, letter: "F", name: "Friday" },
  { dayNum: 6, letter: "S", name: "Saturday" },
  { dayNum: 0, letter: "S", name: "Sunday" },
];

/** Format minutes-from-midnight to "h:mm AM/PM" */
function formatMinutesToTime(minutes: number): string {
  const h = Math.floor(minutes / 60) % 24;
  const m = minutes % 60;
  const period = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 || 12;
  return `${hour12}:${String(m).padStart(2, "0")} ${period}`;
}

function getWarmupStrategyLabel(plan: CustomWarmupPlan | null | undefined): { label: string; value: string } {
  if (!plan?.warmupStrategy) return { label: "—", value: "—" };
  const strategy = plan.warmupStrategy;
  if (strategy === "progressive") {
    const today = plan.currentCounter ?? 0;
    const max = plan.progressiveMaxLimit ?? plan.maxDailyLimit ?? 0;
    return { label: "Progressive", value: max > 0 ? `${today}/${max}` : String(today) };
  }
  if (strategy === "random") {
    const min = plan.randomMinLimit ?? 0;
    const max = plan.randomMaxLimit ?? 0;
    return { label: "Random", value: `${min}–${max}` };
  }
  if (strategy === "flat") {
    const c = plan.flatConstantEmails ?? 0;
    return { label: "Flat", value: String(c) };
  }
  return { label: "—", value: "—" };
}

type EmailStatistic = {
  date: string;
  mailboxtemperature: number;
  opens: number;
  stars: number;
  links_clicked: number;
  replies: number;
  moved_primary: number;
  inbox: number;
  spam: number;
  unspamCount: number;
  send: number;
  gmailOpens: number;
  gmailStars: number;
  gmailEngagementRate: number;
  outlookOpens: number;
  outlookStars: number;
  outlookEngagementRate: number;
  yahooOpens: number;
  yahooStars: number;
  yahooEngagementRate: number;
  workspaceOpens: number;
  workspaceStars: number;
  workspaceEngagementRate: number;
  microsoft365Received: number;
  microsoftOpened: number;
  microsoftFlagged: number;
  microsoftEngagementRate: number;
  zohoOpens: number;
  zohoStars: number;
  zohoEngagementRate: number;
  gmailMovesOutOfSpam: number;
  outlookMovesOutOfSpam: number;
  yahooMovesOutOfSpam: number;
  workspaceMovesOutOfSpam: number;
  microsoftMovesOutOfSpam: number;
  zohoMovesOutOfSpam: number;
  gmailClicks: number;
  outlookClicks: number;
  yahooClicks: number;
  workspaceClicks: number;
  microsoftClicks: number;
  zohoClicks: number;
  senderIps?: string[];
  spf_pass_count:number
  spf_fail_count:number,
              dkim_pass_count:number,
              dkim_fail_count:number,
              dmarc_pass_count:number,
              dmarc_fail_count:number,
  firstEmailBody?: string;
  senderIpHostnames?: Record<
    string,
    {
      hostname: string | null;
      lastLookup?: string;
      lookupError?: string;
    }
  >;
  ipCounts?: Record<string, number>;
  templates?: {
    sender_email: string;
    seed_esp: string;
    sender_esp: string;
    raw_mail: string;
  }[];
};

type MailboxTemplate = NonNullable<EmailStatistic["templates"]>[number];

// Columns for mailbox view
const mailboxColumns: ColDef<EmailStatistic>[] = [
  {
    headerName: "Date",
    field: "date",
    headerTooltip: "Date",
    minWidth: 150,
    valueFormatter: (params) => {
      return new Date(params.value).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
  },
  {
    headerName: "Warmup Email Sent",
    headerTooltip: "Warmup Email Sent",
    field:"send",
    type: "numericColumn",
    minWidth: 180,
    cellRenderer: (params: ValueGetterParams<EmailStatistic>) => {
      const total = Math.max(params?.data?.send ?? 0, params?.data?.opens ?? 0);
      return total.toLocaleString();
    },
  },
  {
    headerName: "Warmup Email Received",
    headerTooltip: "Warmup Email Received",
    field: "opens",
    type: "numericColumn",
    minWidth: 200,
  },
  {
    headerName: "Warmup Email Opens",
    headerTooltip: "Warmup Email Opens",
    field: "opens",
    type: "numericColumn",
    minWidth: 200,
  },
  {
    headerName: "Warmup Email Clicks",
    headerTooltip: "Warmup Email Clicks",
    field: "links_clicked",
    type: "numericColumn",
    minWidth: 200,
  },
  {
    headerName: "Warmup Email Replies",
    headerTooltip: "Warmup Email Replies",
    field: "replies",
    type: "numericColumn",
    minWidth: 200,
  },
  {
  headerName: "Warmup Email Reply Rate",
    headerTooltip: "Warmup Email Reply Rate",
    field: "replies",
    type: "numericColumn",
    minWidth: 200,
    cellRenderer: (params: ValueGetterParams<EmailStatistic>) => {
      return ((params?.data?.replies ?? 0) /( params?.data?.send ?? 0) * 100).toFixed(1) + "%";
    },
  },
  {
    headerName: "Warmup Email Stars",
    headerTooltip: "Warmup Email Stars",
    field: "stars",
    type: "numericColumn",
    minWidth: 200,
  },
  {
    headerName: "Emails Received in Inbox",
    headerTooltip: "Emails Received in Inbox",
    field: "inbox",
    type: "numericColumn",
    minWidth: 200,
  },
  {
    headerName: "Warmup Email Received in Spam",
    headerTooltip: "Total Email Received in Spam",
    field: "spam",
    type: "numericColumn",
    minWidth: 200,
  },
  {
    headerName: "Warmup Email Moved Out of Spam",
    headerTooltip: "Total Email Moved Out of Spam",
    field: "unspamCount",
    type: "numericColumn",
    minWidth: 200,
  },
  {
    headerName: "Engagement Rate",
    headerTooltip: "Engagement Rate",
    // field: "engagement_rate",
    type: "numericColumn",
    minWidth: 200,
    valueGetter: (params: ValueGetterParams<EmailStatistic>) => {
      if (!params.data) return "0%";
     let denominator = 0;
     if (params?.data?.send) {
      denominator = params?.data?.send || 0;
     } 
     if (params?.data?.mailboxtemperature) {
      denominator = Math.min(denominator, params?.data?.mailboxtemperature || 0);
     }
      if (!params.data.opens) return "0%";
      const rawPercent = (params.data.opens / denominator === 0 ? params.data.opens : denominator) * 100;
      const capped = Math.min(rawPercent, 100);
      return `${capped.toFixed(1)}%`;
    },
  },
];

// Columns for sender profile view
const senderProfileColumns: ColDef<EmailStatistic>[] = [
  {
    headerName: "Date",
    field: "date",
    headerTooltip: "Date",
    minWidth: 150,
    valueFormatter: (params) => {
      return new Date(params.value).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
  },
  {
    headerName: "Recevied in Gmail",
    headerTooltip: "Recevied in Gmail",
    field: "gmailOpens",
    type: "numericColumn",
    minWidth: 200,
    cellRenderer: (params: ValueGetterParams<EmailStatistic>) => {
      // For now, show opens as Gmail opens since we don't have provider-specific data
      return (params?.data?.gmailOpens ?? 0).toLocaleString();
    },
  },
  {
    headerName: "Opened in Gmail",
    headerTooltip: "Opened in Gmail",
    field: "gmailOpens",
    type: "numericColumn",
    minWidth: 200,
    cellRenderer: (params: ValueGetterParams<EmailStatistic>) => {
      // For now, show opens as Gmail opens since we don't have provider-specific data
      return (params?.data?.gmailOpens ?? 0).toLocaleString();
    },
  },
  {
    headerName: "Clicks in Gmail",
    headerTooltip: "Clicks in Gmail",
    field: "gmailClicks",
    type: "numericColumn",
    minWidth: 200,
    cellRenderer: (params: ValueGetterParams<EmailStatistic>) => {
      // For now, show total clicks as Gmail clicks since we don't have provider-specific data
      return (params?.data?.gmailClicks ?? 0).toLocaleString();
    },
  },
  {
    headerName: "Mark as Star in Gmail",
    headerTooltip: "Mark as Star in Gmail",
    field: "gmailStars",
    type: "numericColumn",
    minWidth: 200,
    cellRenderer: (params: ValueGetterParams<EmailStatistic>) => {
      // For now, show total clicks as Gmail clicks since we don't have provider-specific data
      return (params?.data?.gmailStars ?? 0).toLocaleString();
    },
  },
  {
    headerName: "Move out of Spam in Gmail",
    headerTooltip: "Move out of Spam in Gmail",
    field: "gmailMovesOutOfSpam",
    type: "numericColumn",
    minWidth: 200,
    cellRenderer: (params: ValueGetterParams<EmailStatistic>) => {
      // For now, show total clicks as Gmail clicks since we don't have provider-specific data
      return (params?.data?.gmailMovesOutOfSpam ?? 0).toLocaleString();
    },
  },
  {
    headerName: "Engagement Rate in Gmail",
    headerTooltip: "Engagement Rate in Gmail",
    field: "gmailEngagementRate",
    type: "numericColumn",
    minWidth: 200,
    valueGetter: (params: ValueGetterParams<EmailStatistic>) => {
      if (!params.data) return "0%";
      return "100%";
    },
  },
  {
    headerName: "Recevied in Outlook",
    headerTooltip: "Recevied in Outlook",
    field: "outlookOpens",
    type: "numericColumn",
    minWidth: 200,
    cellRenderer: (params: ValueGetterParams<EmailStatistic>) => {
      // For now, show opens as Outlook opens since we don't have provider-specific data
      return (params?.data?.outlookOpens ?? 0).toLocaleString();
    },
  },
  {
    headerName: "Opened in Outlook",
    headerTooltip: "Opened in Outlook",
    field: "outlookOpens",
    type: "numericColumn",
    minWidth: 200,
    cellRenderer: (params: ValueGetterParams<EmailStatistic>) => {
      // For now, show opens as Gmail opens since we don't have provider-specific data
      return (params?.data?.outlookOpens ?? 0).toLocaleString();
    },
  },
  {
    headerName: "Clicks in Outlook",
    headerTooltip: "Clicks in Outlook",
    field: "outlookClicks",
    type: "numericColumn",
    minWidth: 200,
    cellRenderer: (params: ValueGetterParams<EmailStatistic>) => {
      // For now, show 0 since we don't have provider-specific data
      return (params?.data?.outlookClicks ?? 0).toLocaleString();
    },
  },
  {
    headerName: "Mark as Flagged Outlook",
    headerTooltip: "Mark as Flagged Outlook",
    field: "outlookStars",
    type: "numericColumn",
    minWidth: 200,
    cellRenderer: (params: ValueGetterParams<EmailStatistic>) => {

  return (params?.data?.outlookStars ?? 0).toLocaleString();
    },
  },  
  {
    headerName: "Move out of Junk in Outlook",
    headerTooltip: "Move out of Junk in Outlook",
    field: "outlookMovesOutOfSpam",
    type: "numericColumn",
    minWidth: 200,
    cellRenderer: (params: ValueGetterParams<EmailStatistic>) => {
      return (params?.data?.outlookMovesOutOfSpam ?? 0).toLocaleString();
    },
  },
  {
    headerName: "Engagement Rate in Outlook",
    headerTooltip: "Engagement Rate in Outlook",
    field: "outlookEngagementRate",
    type: "numericColumn",
    minWidth: 200,
    valueGetter: (params: ValueGetterParams<EmailStatistic>) => {
      // For now, show 0 since we don't have provider-specific data
      if (!params.data) return "0%";
      return "100%";
    },
  },
  {
    headerName: "Total Recevied in Yahoo",
    headerTooltip: "Total Recevied in Yahoo",
    field: "yahooOpens",
    type: "numericColumn",
    minWidth: 200,
    cellRenderer: (params: ValueGetterParams<EmailStatistic>) => {
      // For now, show 0 since we don't have provider-specific data
      return (params?.data?.yahooOpens ?? 0).toLocaleString();
    },
  },
  {
    headerName: "Opened in Yahoo",
    headerTooltip: "Opened in Yahoo",
    field: "yahooOpens",
    type: "numericColumn",
    minWidth: 200,
    cellRenderer: (params: ValueGetterParams<EmailStatistic>) => {
      // For now, show 0 since we don't have provider-specific data
      return (params?.data?.yahooOpens ?? 0).toLocaleString();
    },
  },
  {
    headerName: "Clicks in Yahoo",
    headerTooltip: "Clicks in Yahoo",
    field: "yahooClicks",
    type: "numericColumn",
    minWidth: 200,
    cellRenderer: (params: ValueGetterParams<EmailStatistic>) => {
      // For now, show 0 since we don't have provider-specific data
      return (params?.data?.yahooClicks ?? 0).toLocaleString();
    },
  },
  {
    headerName: "Mark as Star Yahoo",
    headerTooltip: "Mark as Star Yahoo",
    field: "yahooStars",
    type: "numericColumn",
    minWidth: 200,
    cellRenderer: (params: ValueGetterParams<EmailStatistic>) => {
      // For now, show 0 since we don't have provider-specific data
      return (params?.data?.yahooStars ?? 0).toLocaleString();
    },
  },
  {
    headerName: "Move out of Junk in Yahoo",
    headerTooltip: "Move out of Junk in Yahoo",
    field: "yahooMovesOutOfSpam",
    type: "numericColumn",
    minWidth: 200,
    cellRenderer: (params: ValueGetterParams<EmailStatistic>) => {
      return (params?.data?.yahooMovesOutOfSpam ?? 0).toLocaleString();
    },
  },
  {
    headerName: "Engagement Rate in Yahoo",
    headerTooltip: "Engagement Rate in Yahoo",
    field: "yahooEngagementRate",
    type: "numericColumn",
    minWidth: 200,
    valueGetter: (params: ValueGetterParams<EmailStatistic>) => {
      // For now, show 0 since we don't have provider-specific data
      if (!params.data) return "0%";
      return "100%";
    },
  },
  {
    headerName: "Recevied in Google Workspace",
    headerTooltip: "Recevied in Google Workspace",
    field: "workspaceOpens",
    type: "numericColumn",
    minWidth: 200,
    cellRenderer: (params: ValueGetterParams<EmailStatistic>) => {
      // For now, show 0 since we don't have provider-specific data
      return (params?.data?.workspaceOpens ?? 0).toLocaleString();
    },
  },
  {
    headerName: "Opened in Google Workspace",
    headerTooltip: "Opened in Google Workspace",
    field: "workspaceOpens",
    type: "numericColumn",
    minWidth: 200,
    cellRenderer: (params: ValueGetterParams<EmailStatistic>) => {
      // For now, show 0 since we don't have provider-specific data
      return (params?.data?.workspaceOpens ?? 0).toLocaleString();
    },
  },
  {
    headerName: "Clicks in Google Workspace",
    headerTooltip: "Clicks in Google Workspace",
    field: "workspaceClicks",
    type: "numericColumn",
    minWidth: 200,
    cellRenderer: (params: ValueGetterParams<EmailStatistic>) => {
      // For now, show 0 since we don't have provider-specific data
      return (params?.data?.workspaceClicks ?? 0).toLocaleString();
    },
  },
  {
    headerName: "Mark as Star Google Workspace",
    headerTooltip: "Mark as Star Google Workspace",
    field: "workspaceStars",
    type: "numericColumn",
    minWidth: 200,
    cellRenderer: (params: ValueGetterParams<EmailStatistic>) => {
      // For now, show 0 since we don't have provider-specific data
      return (params?.data?.workspaceStars ?? 0).toLocaleString();
    },
  },
  {
    headerName: "Move out of Spam in Google Workspace",
    headerTooltip: "Move out of Spam in Google Workspace",
    field: "workspaceMovesOutOfSpam",
    type: "numericColumn",
    minWidth: 200,
    cellRenderer: (params: ValueGetterParams<EmailStatistic>) => {
      return (params?.data?.workspaceMovesOutOfSpam ?? 0).toLocaleString();
    },
  },
  {
    headerName: "Engagement Rate in Google Workspace",
    headerTooltip: "Engagement Rate in Google Workspace",
    field: "workspaceEngagementRate",
    type: "numericColumn",
    minWidth: 200,
    valueGetter: (params: ValueGetterParams<EmailStatistic>) => {
      // For now, show 0 since we don't have provider-specific data
      if (!params.data) return "0%";
      return "100%";
    },
  },
  {
    headerName: "Recevied in Microsoft 365",
    headerTooltip: "Recevied in Microsoft 365",
    field: "microsoftOpened",
    type: "numericColumn",
    minWidth: 200,
    cellRenderer: (params: ValueGetterParams<EmailStatistic>) => {
      // For now, show 0 since we don't have provider-specific data
      return (params?.data?.microsoftOpened ?? 0).toLocaleString();
    },
  },
  {
    headerName: "Opened in Microsoft 365",
    headerTooltip: "Opened in Microsoft 365",
    field: "microsoftOpened",
    type: "numericColumn",
    minWidth: 200,
    cellRenderer: (params: ValueGetterParams<EmailStatistic>) => {
      // For now, show 0 since we don't have provider-specific data
      return (params?.data?.microsoftOpened ?? 0).toLocaleString();
    },
  },
  {
    headerName: "Clicks in Microsoft 365",
    headerTooltip: "Clicks in Microsoft 365",
    field: "microsoftClicks",
    type: "numericColumn",
    minWidth: 200,
    cellRenderer: (params: ValueGetterParams<EmailStatistic>) => {
      // For now, show 0 since we don't have provider-specific data
      return (params?.data?.microsoftClicks ?? 0).toLocaleString();
    },
  },
  {
    headerName: "Mark as Flagged Microsoft 365",
    headerTooltip: "Mark as Star Microsoft 365",
    field: "microsoftFlagged",
    type: "numericColumn",
    minWidth: 200,
    cellRenderer: (params: ValueGetterParams<EmailStatistic>) => {
      // For now, show 0 since we don't have provider-specific data
      return (params?.data?.microsoftFlagged ?? 0).toLocaleString();
    },
  },
  {
    headerName: "Move out of Spam in Microsoft 365",
    headerTooltip: "Move out of Spam in Microsoft 365",
    field: "microsoftMovesOutOfSpam",
    type: "numericColumn",
    minWidth: 200,
    cellRenderer: (params: ValueGetterParams<EmailStatistic>) => {
      return (params?.data?.microsoftMovesOutOfSpam ?? 0).toLocaleString();
    },
  },
  {
    headerName: "Engagement Rate in Microsoft 365",
    headerTooltip: "Engagement Rate in Microsoft 365",
    field: "microsoftEngagementRate",
    type: "numericColumn",
    minWidth: 200,
    cellRenderer: (params: ValueGetterParams<EmailStatistic>) => {
      // For now, show 0 since we don't have provider-specific data
      if (!params.data) return "0%";
      return "100%";
    },
  },
  {
    headerName: "Recevied in Zoho",
    headerTooltip: "Recevied in Zoho",
    field: "zohoOpens",
    type: "numericColumn",
    minWidth: 200,
    cellRenderer: (params: ValueGetterParams<EmailStatistic>) => {
      return (params?.data?.zohoOpens ?? 0).toLocaleString();
    },
  },
  {
    headerName: "Opened in Zoho",
    headerTooltip: "Opened in Zoho",
    field: "zohoOpens",
    type: "numericColumn",
    minWidth: 200,
    cellRenderer: (params: ValueGetterParams<EmailStatistic>) => {
      return (params?.data?.zohoOpens ?? 0).toLocaleString();
    },
  },
  {
    headerName: "Clicks in Zoho",
    headerTooltip: "Clicks in Zoho",
    field: "zohoClicks",
    type: "numericColumn",
    minWidth: 200,
    cellRenderer: (params: ValueGetterParams<EmailStatistic>) => {
      return (params?.data?.zohoClicks ?? 0).toLocaleString();
    },
  },
  {
    headerName: "Mark as Star Zoho",
    headerTooltip: "Mark as Star Zoho",
    field: "zohoStars",
    type: "numericColumn",
    minWidth: 200,
    cellRenderer: (params: ValueGetterParams<EmailStatistic>) => {
      return (params?.data?.zohoStars ?? 0).toLocaleString();
    },
  },
  {
    headerName: "Move out of Junk in Zoho",
    headerTooltip: "Move out of Junk in Zoho",
    field: "zohoMovesOutOfSpam",
    type: "numericColumn",
    minWidth: 200,
    cellRenderer: (params: ValueGetterParams<EmailStatistic>) => {
      return (params?.data?.zohoMovesOutOfSpam ?? 0).toLocaleString();
    },
  },
  {
    headerName: "Engagement Rate in Zoho",
    headerTooltip: "Engagement Rate in Zoho",
    field: "zohoEngagementRate",
    type: "numericColumn",
    minWidth: 200,
    valueGetter: (params: ValueGetterParams<EmailStatistic>) => {
      if (!params.data) return "0%";
      return "100%";
    },
  },
  {
    headerName: "Email Template Sent",
    headerTooltip: "Email Template Sent",
    field: "firstEmailBody",
    type: "textColumn",
    minWidth: 200,
  },
];

export const Route = createFileRoute("/_private/mailboxes/$mailboxid")({
  component: RouteComponent,
  loader: async ({ params: { mailboxid }, location }) => {
    // @ts-expect-error
    return { mailboxId: mailboxid, sender: location.search.sender ?? "" };
  },
});

function RouteComponent() {
  const { mailboxid } = useParams({ from: "/_private/mailboxes/$mailboxid" });
  // @ts-expect-error
  const { sender } = useSearch({ from: "/_private/mailboxes/$mailboxid" });
  const [showSettings, setShowSettings] = useState(false);
  const [showB2BSettings, setShowB2BSettings] = useState(false);
  /** When true, saving B2B settings will also enable warmup (used when user turns warmup switch ON). */
  const [enableWarmupAfterSave, setEnableWarmupAfterSave] = useState(false);
  const [date, setDate] = useState<DateRange | undefined>({
    from: subDays(new Date(), 7),
    to: new Date(),
  });
  const [showSenderIpsDialog, setShowSenderIpsDialog] = useState(false);
  const [selectedSenderIps, setSelectedSenderIps] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [showEmailTemplateDialog, setShowEmailTemplateDialog] = useState(false);
  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState<string>("");
  const [selectedSenderIpHostnames, setSelectedSenderIpHostnames] = useState<
    EmailStatistic["senderIpHostnames"]
  >({});
  const [selectedIpCounts, setSelectedIpCounts] = useState<
    EmailStatistic["ipCounts"]
  >({});
  const [selectedTemplates, setSelectedTemplates] = useState<
    EmailStatistic["templates"]
  >([]);
  const [activeTemplateIndex, setActiveTemplateIndex] = useState(0);
  const [parsedEmail, setParsedEmail] = useState<ParsedEmail | null>(null);
  const [isParsingEmail, setIsParsingEmail] = useState(false);
  const [selectedTemplateForPreview, setSelectedTemplateForPreview] =
    useState<MailboxTemplate | null>(null);
  const [expandedEsps, setExpandedEsps] = useState<Set<string>>(new Set());
  const [isReceivingChainOpen, setIsReceivingChainOpen] = useState(true);
  const [selectedSenderEsp, setSelectedSenderEsp] = useState<string>("");
  const [downloadingDate, setDownloadingDate] = useState<string | null>(null);
  const { mutate: downloadFailedHeaders, isPending: isDownloading } = useDownloadFailedHeaderssenders();

  const groupedTemplatesByEsp = useMemo(() => {
    if (!selectedTemplates || selectedTemplates.length === 0) return {};
    return selectedTemplates.reduce((acc, template) => {
      const espKey =
        (template as any).seed_esp ||
        (template as any).sender_esp ||
        "Unknown ESP";
      if (!acc[espKey]) {
        acc[espKey] = [];
      }
      acc[espKey].push(template as MailboxTemplate);
      return acc;
    }, {} as Record<string, MailboxTemplate[]>);
  }, [selectedTemplates]);

  const { mutate: updateMailbox, mutateAsync: updateMailboxAsync, isPending: updatingMailbox } =
    useUpdateMailbox();

  const { data: mailbox, isLoading: isLoadingMailbox } =
    useMailboxesbyId(mailboxid);
  const { data: b2bSettings } = useGetB2BWarmupSettings(
    mailboxid && !sender ? mailboxid : null
  );
  const { mutateAsync: updateMailboxWarmup, isPending: updatingMailboxWarmup } =
    useUpdateMailboxWarmup();
  const { mutateAsync: enableWarmupWithSettings } = useEnableWarmupWithSettings();

  const { hasPermission } = usePermission();

  // State for seed selection (must be before sender ESP effect)
  const [selectedSeeds, setSelectedSeeds] = useState<string[]>([]);
  const previousSelectedSeedsRef = useRef<string[]>([]);

  const senderEmail = sender ? (sender || mailbox?.email || "") : "";
  const { data: senderEspTypes = [], isLoading: isLoadingSenderEspTypes } =
    useSenderEspTypes(senderEmail);

  // Deduplicate ESP list (API may return same ESP multiple times, e.g. "Private SMTP" twice)
  const uniqueSenderEspTypes = useMemo(() => {
    const seen = new Set<string>();
    return (senderEspTypes ?? []).filter((esp) => {
      const key = String(esp).trim().toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [senderEspTypes]);

  // Map API ESP type string to seed value (for column/card filtering)
  const espToSeedMap: Record<string, string> = useMemo(
    () => ({
      Gmail: "personal-gmail",
      Outlook: "personal-outlook",
      Yahoo: "personal-yahoo",
      "Google Workspace": "Google-Workspace",
      "Google-Workspace": "Google-Workspace",
      "Microsoft 365": "Microsoft365",
      Microsoft365: "Microsoft365",
      Zoho: "personal-zoho",
    }),
    []
  );

  // When sender: set default ESP from sender profile (mailbox.espType/authProvider) for dropdown only; do not change Seed Assigned
  useEffect(() => {
    if (!sender) return;
    if (uniqueSenderEspTypes.length > 0 && !selectedSenderEsp) {
      // Use sender's default ESP from mailbox if it exists in list, else first from list (dropdown only)
      const senderDefaultEsp = (mailbox as any)?.espType || mailbox?.authProvider;
      const match =
        senderDefaultEsp &&
        uniqueSenderEspTypes.find(
          (esp) => String(esp).toLowerCase() === String(senderDefaultEsp).toLowerCase()
        );
      const defaultEsp = match ?? uniqueSenderEspTypes[0];
      setSelectedSenderEsp(defaultEsp);
    }
  }, [sender, selectedSenderEsp, uniqueSenderEspTypes, mailbox]);

  // Sender's default ESP: from mailbox.espType/authProvider if in list, else first from API
  const senderDefaultEsp = useMemo(() => {
    if (!sender || !uniqueSenderEspTypes?.length) return "";
    const fromMailbox = (mailbox as any)?.espType || mailbox?.authProvider;
    const match =
      fromMailbox &&
      uniqueSenderEspTypes.find(
        (esp) => String(esp).toLowerCase() === String(fromMailbox).toLowerCase()
      );
    return match ?? uniqueSenderEspTypes[0] ?? "";
  }, [sender, uniqueSenderEspTypes, mailbox]);

  // Effective ESP for sender view: selected dropdown value or sender's default
  const effectiveSenderEsp = sender
    ? (selectedSenderEsp || senderDefaultEsp)
    : undefined;

  const {
    data: mailboxStats,
    isLoading: isLoadingMailboxStats,
    isError: isErrorMailboxStats,
  } = useMailboxDailyStats({
    mailboxId: mailboxid,
    isMailboxStats: !sender,
    startDate: date?.from ? formatDateLocal(date.from) : "",
    endDate: date?.to ? formatDateLocal(date.to) : "",
    espType: effectiveSenderEsp || undefined,
  });

  const todayGmailReplies = useMemo(() => {
    return mailboxStats?.dailyStats.find((stat) => stat.date === (date?.to ? formatDateLocal(date.to) : ""))?.gmailReplies || 0;
  }, [mailboxStats, date]);
  const todayOutlookReplies = useMemo(() => {
    return mailboxStats?.dailyStats.find((stat) => stat.date === (date?.to ? formatDateLocal(date.to) : ""))?.outlookReplies || 0;
  }, [mailboxStats, date]);

  const todayYahooReplies = useMemo(() => {
    return mailboxStats?.dailyStats.find((stat) => stat.date === (date?.from ? formatDateLocal(date.from) : ""))?.yahooReplies || 0;
  }, [mailboxStats, date]);

  const { mutateAsync: manageSeedEspTypes, isPending: isManagingSeeds } =
    useManageSeedEspTypes();

  // Map UI seed values to API ESP type values
  const mapSeedToEspType = (seed: string): string => {
    const mapping: Record<string, string> = {
      "personal-gmail": "Gmail",
      "personal-outlook": "Outlook",
      "personal-yahoo": "Yahoo",
      "Google-Workspace": "Google-Workspace",
      "Microsoft365": "Microsoft365",
      "personal-zoho": "Zoho",
    };
    return mapping[seed] || seed;
  };

  // Initialize Seed Assigned from mailbox.seedEspTypes (API response) so checkboxes show correct state
  useEffect(() => {
    const seedEspTypes = (mailbox as any)?.seedEspTypes;
    if (seedEspTypes && Array.isArray(seedEspTypes)) {
      const reverseMapping: Record<string, string> = {
        Gmail: "personal-gmail",
        Outlook: "personal-outlook",
        Yahoo: "personal-yahoo",
        "Google Workspace": "Google-Workspace",
        "Microsoft 365": "Microsoft365",
        Zoho: "personal-zoho",
      };
      const initialSeeds = seedEspTypes
        .map((espType: string) => reverseMapping[espType] || espType)
        .filter((seed: string) => seed !== undefined);
      if (initialSeeds.length > 0) {
        setSelectedSeeds(initialSeeds);
        previousSelectedSeedsRef.current = [...initialSeeds];
      }
    }
  }, [mailbox]);

  const handleSeedChange = async (value: string) => {
    const previousSeeds = [...selectedSeeds];
    const newSeeds = previousSeeds.includes(value)
      ? previousSeeds.filter((v) => v !== value)
      : [...previousSeeds, value];

    // Update UI immediately
    setSelectedSeeds(newSeeds);

    // Determine the operation (add or remove)
    const operation = previousSeeds.includes(value) ? "remove" : "add";
    const espType = mapSeedToEspType(value);

    try {
      await manageSeedEspTypes({
        id: mailboxid,
        dto: {
          actions: [
            {
              espType,
              operation,
            },
          ],
        },
      });

      // Update the ref to track the new state
      previousSelectedSeedsRef.current = newSeeds;
    } catch (error) {
      // Revert UI state on error
      setSelectedSeeds(previousSelectedSeedsRef.current);
      const errorMessage = extractErrorMessage(
        error,
        "Failed to update seed ESP types"
      );
      toast.error(errorMessage);
    }
  };

  // Sender IPs Cell Renderer Component
  const SenderIpsCellRenderer = (params: ValueGetterParams<EmailStatistic>) => {
    const senderIps = params?.data?.senderIps || [];
    const hasIps = senderIps.length > 0;

    const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      if (hasIps) {
        setSelectedSenderIps(senderIps);
        setSelectedDate(params.data?.date || "");
        const data = params.data as EmailStatistic;
        setSelectedSenderIpHostnames(data?.senderIpHostnames || {});
        setSelectedIpCounts(data?.ipCounts || {});
        setShowSenderIpsDialog(true);
      }
    };

    return (
      <button
        className={`px-2 py-1 rounded text-sm transition-colors ${
          hasIps
            ? "bg-primary text-primary-foreground hover:bg-primary/90 cursor-pointer"
            : "bg-muted text-muted-foreground cursor-not-allowed opacity-50"
        }`}
        disabled={!hasIps}
        onClick={handleClick}
      >
        {hasIps ? `${senderIps.length} IP${senderIps.length > 1 ? "s" : ""}` : "No IPs"}
      </button>
    );
  };

  const SpfCellRenderer = (params: ValueGetterParams<EmailStatistic>) => {
    const totalspfPass = (params?.data as any)?.spf_pass_count || 0;
    const totalspfFail = (params?.data as any)?.spf_fail_count || 0;
    const totalspf = totalspfPass + totalspfFail;
    const percentage = totalspf > 0 ? (totalspfPass / totalspf) * 100 : 0;

    let Icon = CheckCircle;
    let colorClass = "text-green-600";

    if (percentage >= 95) {
      Icon = CheckCircle;
      colorClass = "text-green-600";
    } else if (percentage >= 90) {
      Icon = CheckCircle;
      colorClass = "text-yellow-500";
    } else {
      Icon = AlertCircle;
      colorClass = "text-red-500";
    }

    const handleDownload = (e: React.MouseEvent) => {
      e.stopPropagation();
      const date = (params?.data as any)?.date;
      const email = mailbox?.email || sender;
      if (date) {
        setDownloadingDate(date);
        downloadFailedHeaders({ date, email }, {
          onSuccess: (data) => {
            const url = window.URL.createObjectURL(new Blob([data as any]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `failed-headers-${date}.zip`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            setDownloadingDate(null);
            toast.success("Download started");
          },
          onError: () => {
            setDownloadingDate(null);
            toast.error("Failed to download headers");
          }
        });
      }
    };

    const isThisDownloading = isDownloading && downloadingDate === (params?.data as any)?.date;

    return (
      <div className="flex items-center mt-2 gap-2">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-2 cursor-help">
                <Icon className={`h-5 w-5 ${colorClass}`} />
                <span className="font-medium text-sm">{percentage.toFixed(2)}%</span>
              </div>
            </TooltipTrigger>
            <TooltipContent className="bg-slate-900 text-white border-none">
              <p>{totalspfPass} passed, {totalspfFail} failed</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {totalspfFail > 0 && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 ml-2"
            onClick={handleDownload}
            disabled={isThisDownloading}
          >
            {isThisDownloading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4 text-muted-foreground hover:text-foreground" />
            )}
          </Button>
        )}
      </div>
    );
  };

  const DmarcCellRenderer = (params: ValueGetterParams<EmailStatistic>) => {
    const totaldmarcPass = (params?.data as any)?.dmarc_pass_count || 0;
    const totaldmarcFail = (params?.data as any)?.dmarc_fail_count || 0;
    const totaldmarc = totaldmarcPass + totaldmarcFail;
    const percentage = totaldmarc > 0 ? (totaldmarcPass / totaldmarc) * 100 : 0;

    let Icon = CheckCircle;
    let colorClass = "text-green-600";

    if (percentage >= 95) {
      Icon = CheckCircle;
      colorClass = "text-green-600";
    } else if (percentage >= 90) {
      Icon = CheckCircle;
      colorClass = "text-yellow-500";
    } else {
      Icon = AlertCircle;
      colorClass = "text-red-500";
    }

    const handleDownload = (e: React.MouseEvent) => {
      e.stopPropagation();
      const date = (params?.data as any)?.date;
      const email = mailbox?.email || sender;
      if (date) {
        setDownloadingDate(date);
        downloadFailedHeaders({ date, email }, {
          onSuccess: (data) => {
            const url = window.URL.createObjectURL(new Blob([data as any]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `failed-headers-${date}.zip`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            setDownloadingDate(null);
            toast.success("Download started");
          },
          onError: () => {
            setDownloadingDate(null);
            toast.error("Failed to download headers");
          }
        });
      }
    };

    const isThisDownloading = isDownloading && downloadingDate === (params?.data as any)?.date;

    return (
      <div className="flex items-center mt-2 gap-2">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-2 cursor-help">
                <Icon className={`h-5 w-5 ${colorClass}`} />
                <span className="font-medium text-sm">{percentage.toFixed(2)}%</span>
              </div>
            </TooltipTrigger>
            <TooltipContent className="bg-slate-900 text-white border-none">
              <p>{totaldmarcPass} passed, {totaldmarcFail} failed</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {totaldmarcFail > 0 && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 ml-2"
            onClick={handleDownload}
            disabled={isThisDownloading}
          >
            {isThisDownloading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4 text-muted-foreground hover:text-foreground" />
            )}
          </Button>
        )}
      </div>
    );
  };

  const DkimCellRenderer = (params: ValueGetterParams<EmailStatistic>) => {
    const totaldkimPass = (params?.data as any)?.dkim_pass_count || 0;
    const totaldkimFail = (params?.data as any)?.dkim_fail_count || 0;
    const totaldkim = totaldkimPass + totaldkimFail;
    const percentage = totaldkim > 0 ? (totaldkimPass / totaldkim) * 100 : 0;

    let Icon = CheckCircle;
    let colorClass = "text-green-600";

    if (percentage >= 95) {
      Icon = CheckCircle;
      colorClass = "text-green-600";
    } else if (percentage >= 90) {
      Icon = CheckCircle;
      colorClass = "text-yellow-500";
    } else {
      Icon = AlertCircle;
      colorClass = "text-red-500";
    }

    const handleDownload = (e: React.MouseEvent) => {
      e.stopPropagation();
      const date = (params?.data as any)?.date;
      const email = mailbox?.email || sender;
      if (date) {
        setDownloadingDate(date);
        downloadFailedHeaders({ date, email }, {
          onSuccess: (data) => {
            const url = window.URL.createObjectURL(new Blob([data as any]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `failed-headers-${date}.zip`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            setDownloadingDate(null);
            toast.success("Download started");
          },
          onError: () => {
            setDownloadingDate(null);
            toast.error("Failed to download headers");
          }
        });
      }
    };

    const isThisDownloading = isDownloading && downloadingDate === (params?.data as any)?.date;

    return (
      <div className="flex items-center mt-2 gap-2">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-2 cursor-help">
                <Icon className={`h-5 w-5 ${colorClass}`} />
                <span className="font-medium text-sm">{percentage.toFixed(2)}%</span>
              </div>
            </TooltipTrigger>
            <TooltipContent className="bg-slate-900 text-white border-none">
              <p>{totaldkimPass} passed, {totaldkimFail} failed</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {totaldkimFail > 0 && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 ml-2"
            onClick={handleDownload}
            disabled={isThisDownloading}
          >
            {isThisDownloading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4 text-muted-foreground hover:text-foreground" />
            )}
          </Button>
        )}
      </div>
    );
  };
  // Email Template Cell Renderer Component
  const EmailTemplateCellRenderer = (params: ValueGetterParams<EmailStatistic>) => {
    const emailTemplate = (params?.data as any)?.firstEmailBody || "";
    const templates = (params?.data as any)?.templates || [];
    const hasTemplate =
      (emailTemplate && emailTemplate.trim().length > 0) ||
      (Array.isArray(templates) && templates.length > 0);

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      if (hasTemplate) {
        // Prefer explicit templates array if available, fall back to single body
        if (Array.isArray(templates) && templates.length > 0) {
          setSelectedTemplates(templates);
          setActiveTemplateIndex(0);
          // Clear previously parsed email so it can be re-parsed for this selection
          setParsedEmail(null);
          setSelectedEmailTemplate("");
          setSelectedTemplateForPreview(null);
          setExpandedEsps(new Set());
          setIsReceivingChainOpen(true);
        } else {
          setSelectedTemplates([]);
          setSelectedEmailTemplate(emailTemplate);
        }
        setShowEmailTemplateDialog(true);
      }
    };

    return (
      <button
        className={`px-2 py-1 rounded text-sm transition-colors ${
          hasTemplate
            ? "bg-primary text-primary-foreground hover:bg-primary/90 cursor-pointer"
            : "bg-muted text-muted-foreground cursor-not-allowed opacity-50"
        }`}
        disabled={!hasTemplate}
        onClick={handleClick}
      >
        {hasTemplate ? "View Template" : "No Template"}
      </button>
    );
  };

  // Filter columns based on selected seeds
  const columns = useMemo(() => {
    // Create sender IPs column with handler
    const senderIpsColumn: ColDef<EmailStatistic> = {
      headerName: "Sender IPs",
      headerTooltip: "Sender IPs",
      field: "senderIps",
      minWidth: 150,
      cellRenderer: SenderIpsCellRenderer,
    };

    const spfColums: ColDef<EmailStatistic> = {
      headerName: "SPF",
      headerTooltip: "SPF",
      field: "spf_pass_count",
      minWidth: 150,
      cellRenderer: SpfCellRenderer,
    };

    const dmarcColums: ColDef<EmailStatistic> = {
      headerName: "DMARC",
      headerTooltip: "DMARC",
      field: "dmarc_pass_count",
      minWidth: 150,
      cellRenderer: DmarcCellRenderer,
    };

    const dkimColums: ColDef<EmailStatistic> = {
      headerName: "DKIM",
      headerTooltip: "DKIM",
      field: "dkim_pass_count",
      minWidth: 150,
      cellRenderer: DkimCellRenderer,
    };

    if (!sender) {
      return [...mailboxColumns, senderIpsColumn,spfColums,dkimColums,dmarcColums];
    }

    // Sender view: show only columns for selected seeds (filter by ESP)
    const seedToEspMap: Record<string, string> = {
      "personal-gmail": "Gmail",
      "personal-outlook": "Outlook",
      "personal-yahoo": "Yahoo",
      "Google-Workspace": "Google Workspace",
      "Microsoft365": "Microsoft 365",
      "personal-zoho": "Zoho",
    };
    const selectedEsps = selectedSeeds
      .map((seed) => seedToEspMap[seed])
      .filter(Boolean);

    const filteredColumns =
      selectedSeeds.length === 0
        ? [senderProfileColumns[0]] // only Date when nothing selected
        : senderProfileColumns.filter((col) => {
            if (col.field === "date" || col.field === "firstEmailBody") return true;
            const headerName = col.headerName || "";
            return selectedEsps.some((esp) => headerName.includes(esp));
          });

    const columnsWithRenderers = filteredColumns.map((col) => {
      if (col.field === "firstEmailBody") {
        return {
          ...col,
          cellRenderer: EmailTemplateCellRenderer,
        };
      }
      return col;
    });
    return [...columnsWithRenderers,spfColums,dkimColums,dmarcColums, senderIpsColumn];
  }, [sender, selectedSeeds]);

  // Combine dailyStats and emailPlacementByDate to create proper EmailStatistic data
  const combinedTableData = useMemo(() => {
    if (!mailboxStats?.dailyStats || !mailboxStats?.emailPlacementByDate) {
      return [];
    }

    // Create a map of placement data by date for quick lookup
    const placementByDate = new Map(
      mailboxStats.emailPlacementByDate.map((placement) => [
        placement.date,
        placement,
      ])
    );

    // Combine daily stats with placement data
    return mailboxStats.dailyStats.map((dailyStat) => {
      const placementData = placementByDate.get(dailyStat.date);

      return {
        date: dailyStat.date,
        send: dailyStat.send || 0,
        opens: dailyStat.opens || 0,
        stars: dailyStat.stars || 0,
        links_clicked: dailyStat.links_clicked || 0,
        replies: dailyStat.replies || 0,
        moved_primary: dailyStat.movedToPrimary || 0,
        inbox: (dailyStat.opens || 0) - (dailyStat.spam || 0),
        spam: placementData?.spamTotal || dailyStat.spam || 0,
        unspamCount: dailyStat.unspamCount || 0,
        mailboxtemperature: dailyStat.mailboxtemperature || 0,
        gmailOpens: dailyStat.gmailOpens || 0,
        gmailStars: dailyStat.gmailStars || 0,
        gmailClicks: dailyStat.gmailClicks || 0,
        gmailEngagementRate: dailyStat.gmailEngagementRate || 0,
      gmailMovesOutOfSpam: (dailyStat as any)?.gmailMovesOutOfSpam || 0,
        outlookOpens: dailyStat.outlookOpens || 0,
        outlookStars: dailyStat.outlookStars || 0,
        outlookClicks: dailyStat.outlookClicks || 0,
        outlookEngagementRate: dailyStat.outlookEngagementRate || 0,
      outlookMovesOutOfSpam: (dailyStat as any)?.outlookMovesOutOfSpam || 0,
        yahooOpens: dailyStat.yahooOpens || 0,
        yahooStars: dailyStat.yahooStars || 0,
        yahooEngagementRate: dailyStat.yahooEngagementRate || 0,
        yahooClicks: dailyStat.yahooClicks || 0,
      yahooMovesOutOfSpam: (dailyStat as any)?.yahooMovesOutOfSpam || 0,
        workspaceOpens: dailyStat.workspaceOpens || 0,
        workspaceStars: dailyStat.workspaceStars || 0,
        workspaceClicks: dailyStat.workspaceClicks || 0,
        workspaceEngagementRate: dailyStat.workspaceEngagementRate || 0,
      workspaceMovesOutOfSpam: (dailyStat as any)?.workspaceMovesOutOfSpam || 0,
        microsoftOpened: dailyStat.microsoftOpens || 0,
        microsoftFlagged: dailyStat.microsoftStars || 0,
        microsoftEngagementRate: dailyStat.microsoftEngagementRate || 0,
      microsoftMovesOutOfSpam: (dailyStat as any)?.microsoftMovesOutOfSpam || 0,
      microsoftClicks: dailyStat.microsoftClicks || 0,
      zohoOpens: (dailyStat as any)?.zohoOpens || 0,
      zohoStars: (dailyStat as any)?.zohoStars || 0,
      zohoClicks: (dailyStat as any)?.zohoClicks || 0,
      zohoEngagementRate: (dailyStat as any)?.zohoEngagementRate || 0,
      zohoMovesOutOfSpam: (dailyStat as any)?.zohoMovesOutOfSpam || 0,
      spf_pass_count:(dailyStat as any)?.spf_pass_count || 0,
      dkim_fail_count: (dailyStat as any)?.dkim_fail_count || 0,
    dkim_pass_count: (dailyStat as any)?.dkim_pass_count || 0,
    dmarc_fail_count: (dailyStat as any)?.dmarc_fail_count || 0,
    dmarc_pass_count: (dailyStat as any)?.dmarc_pass_count || 0,
    spf_fail_count: (dailyStat as any)?.spf_fail_count || 0,
      senderIps: (dailyStat as any)?.senderIps || [],
        firstEmailBody: (dailyStat as any)?.firstEmailBody || "",
        senderIpHostnames: (dailyStat as any)?.senderIpHostnames || {},
        ipCounts: (dailyStat as any)?.ipCounts || {},
        templates: (dailyStat as any)?.templates || [],
      };
    });
  }, [mailboxStats]);

  // // For exporting
  // const { mutateAsync: exportInteractions } = useExportSenderInteractions();

  // Call export

  const { data: senderMailboxStats, isLoading: isLoadingSenderMailboxStats } =
    useSenderMailboxStats(
      mailbox?._id.toString() ?? "",
      date?.from ? formatDateLocal(date.from) : "",
      date?.to ? formatDateLocal(date.to) : "",
      effectiveSenderEsp || undefined
    ) as { data: SenderMailboxStats | undefined; isLoading: boolean };

  // Gmail Stats
  const gmailStats = useMemo(() => {
    if (!senderMailboxStats?.total?.gmail?.categories) return [];
    
    const gmail = senderMailboxStats.total.gmail.categories;
    return [
      {
        name: "Primary",
        percentage: gmail.percentages?.primary || 0,
        score: gmail.primary || 0,
      },
      {
        name: "Promotions",
        percentage: gmail.percentages?.promotions || 0,
        score: gmail.promotions || 0,
      },
      {
        name: "Updates",
        percentage: gmail.percentages?.updates || 0,
        score: gmail.updates || 0,
      },
      {
        name: "Spam",
        percentage: gmail.percentages?.spam || 0,
        score: gmail.spam || 0,
      },
    ];
  }, [senderMailboxStats]);

  // Google Workspace Stats
  const workspaceStats = useMemo(() => {
    if (!senderMailboxStats?.total?.workspace?.categories) return [];
    
    const workspace = senderMailboxStats.total.workspace.categories;
    return [
      {
        name: "Primary",
        percentage: workspace.percentages?.primary || 0,
        score: workspace.primary || 0,
      },
      {
        name: "Promotions",
        percentage: workspace.percentages?.promotions || 0,
        score: workspace.promotions || 0,
      },
      {
        name: "Updates",
        percentage: workspace.percentages?.updates || 0,
        score: workspace.updates || 0,
      },
      {
        name: "Spam",
        percentage: workspace.percentages?.spam || 0,
        score: workspace.spam || 0,
      },
    ];
  }, [senderMailboxStats]);

  // Outlook Stats
  const outlookStats = useMemo(() => {
    if (!senderMailboxStats?.total?.outlook?.categories) return [];
    
    const outlook = senderMailboxStats.total.outlook.categories;
    return [
      {
        name: "Focused",
        percentage: outlook.percentages?.focused || 0,
        score: outlook.focused || 0,
      },
      {
        name: "Junk",
        percentage: outlook.percentages?.junk || 0,
        score: outlook.junk || 0,
      },
    ];
  }, [senderMailboxStats]);

  // Yahoo Stats
  const yahooStats = useMemo(() => {
    if (!senderMailboxStats?.total?.yahoo?.categories) return [];
    
    const yahoo = senderMailboxStats.total.yahoo.categories;
    return [
      {
        name: "Inbox",
        percentage: yahoo.percentages?.inbox || 0,
        score: yahoo.inbox || 0,
      },
      {
        name: "Spam",
        percentage: yahoo.percentages?.junk || 0,
        score: yahoo.junk || 0,
      },
    ];
  }, [senderMailboxStats]);

  // Microsoft 365 Stats
  const microsoftStats = useMemo(() => {
    if (!senderMailboxStats?.total?.microsoft?.categories) return [];
    
    const microsoft = senderMailboxStats.total.microsoft.categories;
    return [
      {
        name: "Focused",
        percentage: microsoft.percentages?.focused || 0,
        score: microsoft.focused || 0,
      },
      {
        name: "Junk",
        percentage: microsoft.percentages?.junk || 0,
        score: microsoft.junk || 0,
      },
    ];
  }, [senderMailboxStats]);

  // Zoho Stats
  const zohoStats = useMemo(() => {
    if (!(senderMailboxStats?.total as any)?.zoho?.categories) return [];
    
    const zoho = (senderMailboxStats?.total as any).zoho.categories;
    return [
      {
        name: "Inbox",
        percentage: zoho.percentages?.inbox || 0,
        score: zoho.inbox || 0,
      },
      {
        name: "Spam",
        percentage: zoho.percentages?.junk || 0,
        score: zoho.junk || 0,
      },
    ];
  }, [senderMailboxStats]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const gridRef = useRef<AgGridReact>(null);
  const [isExporting, setIsExporting] = useState(false);

  // Aggregate email count for currently selected IPs
  const totalSenderIpEmails = useMemo(() => {
    if (!selectedSenderIps.length || !selectedIpCounts) return 0;
    return selectedSenderIps.reduce((sum, ip) => {
      const sanitizedKey = ip.replace(/[.:]/g, "_");
      const count =
        selectedIpCounts[ip] ??
        selectedIpCounts[sanitizedKey] ??
        0;
      return sum + (typeof count === "number" ? count : 0);
    }, 0);
  }, [selectedSenderIps, selectedIpCounts]);

  // Parse the currently selected template (preview view) into a structured format
  useEffect(() => {
    if (!showEmailTemplateDialog || !selectedTemplateForPreview) {
      setParsedEmail(null);
      setIsParsingEmail(false);
      return;
    }

    const template = selectedTemplateForPreview as any;
    if (!template || !template.raw_mail) {
      // No raw email available, fallback to legacy HTML/body if present
      setParsedEmail(null);
      setSelectedEmailTemplate(
        template?.body || template?.firstEmailBody || ""
      );
      return;
    }

    let isCancelled = false;
    const parse = async () => {
      try {
        setIsParsingEmail(true);
        const parsed = await parseRawEmail(template.raw_mail);
        if (isCancelled) return;
        setParsedEmail(parsed);
        setSelectedEmailTemplate(parsed.html || "");
      } catch (error) {
        console.error("Error parsing email template:", error);
        if (isCancelled) return;
        // Best-effort fallback to raw HTML if available
        setParsedEmail(null);
        setSelectedEmailTemplate("");
      } finally {
        if (!isCancelled) {
          setIsParsingEmail(false);
        }
      }
    };

    parse();

    return () => {
      isCancelled = true;
    };
  }, [showEmailTemplateDialog, selectedTemplateForPreview]);

  const exportToCSV = async () => {
    try {
      setIsExporting(true);
      const displayed = gridRef.current?.api.getAllDisplayedColumns?.() || [];
      const columnKeys = displayed.map((c: any) => c.getColId?.() ?? c.colId);
      gridRef.current?.api.exportDataAsCsv({
        fileName: `DailyStats-${mailbox?.email}.csv`,
        columnKeys,
      });
      toast.success("Export successful");
      window.cioanalytics?.track("mailboxes_interactions_exported", {
        email: mailbox?.email,
      });
    } catch (error) {
      toast.error("Failed to export data");
    } finally {
      setIsExporting(false);
    }
  };

  const toggleEsp = (esp: string) => {
    setExpandedEsps((prev) => {
      const next = new Set(prev);
      if (next.has(esp)) {
        next.delete(esp);
      } else {
        next.add(esp);
      }
      return next;
    });
  };

  const handleTemplateSelect = (template: MailboxTemplate) => {
    setSelectedTemplateForPreview(template);
    setParsedEmail(null);
    setSelectedEmailTemplate("");
    setIsReceivingChainOpen(true);
  };

  const handleBackFromTemplatePreview = () => {
    setSelectedTemplateForPreview(null);
    setParsedEmail(null);
    setIsParsingEmail(false);
    setIsReceivingChainOpen(true);
  };

  const handleMailboxWarmupToggle = async (checked: boolean) => {
    if (
      mailbox?.targetAudience === undefined ||
      mailbox?.targetAudience === null
    ) {
      toast.error("Target audience selection is required to start warm-up.");
      return;
    }

    if (
      !mailbox?.subscriptionId ||
      (!mailbox?.addonPlan && !mailbox?.planId)
    ) {
      toast.error(
        "Please assign a subscription to the mailbox before enabling warm-up."
      );
      return;
    }

    if (checked) {
      // Turning ON: open B2B settings dialog; warmup is enabled only after user saves settings
      setEnableWarmupAfterSave(true);
      setShowB2BSettings(true);
      return;
    }

    await updateMailboxWarmup({
      id: mailboxid,
      mailboxData: {
        isWarmupEnabled: false,
      },
    });

    window.cioanalytics?.track("mailbox_warmup_toggled_off", {
      email: mailbox?.email,
    });
  };

  const handleB2BSaveAndEnableWarmup = useCallback(
    async (settings: B2BWarmupSettings) => {
      try {
        await enableWarmupWithSettings({ mailboxId: mailboxid, settings });
        setShowB2BSettings(false);
        setEnableWarmupAfterSave(false);
        window.cioanalytics?.track("mailbox_warmup_toggled_on", {
          email: mailbox?.email,
        });
      } catch (e) {
        throw e;
      }
    },
    [enableWarmupWithSettings, mailboxid, mailbox?.email]
  );

  /** Build B2B form overrides from mailbox (customWarmupPlan + root) so the settings dialog shows actual saved values */
  const mailboxWarmupOverrides = useMemo((): Partial<B2BWarmupSettings> | null => {
    if (!mailbox) return null;
    const plan = mailbox.customWarmupPlan;
    const m = mailbox as unknown as Record<string, unknown>;
    const out: Partial<B2BWarmupSettings> = {};
    if (plan) {
      if (plan.warmupStrategy != null) out.warmupStrategy = plan.warmupStrategy;
      if (plan.progressiveStartingVolume != null) out.progressiveStartingVolume = plan.progressiveStartingVolume;
      if (plan.progressiveIncreaseBy != null) out.progressiveIncreaseBy = plan.progressiveIncreaseBy;
      if (plan.progressiveMaxLimit != null) out.progressiveMaxLimit = plan.progressiveMaxLimit;
      if (plan.randomMinLimit != null) out.randomMinLimit = plan.randomMinLimit;
      if (plan.randomMaxLimit != null) out.randomMaxLimit = plan.randomMaxLimit;
      if (plan.flatConstantEmails != null) out.flatConstantEmails = plan.flatConstantEmails;
    }
    if (m.settingsMode != null) out.settingsMode = m.settingsMode as B2BWarmupSettings["settingsMode"];
    if (m.timezone != null) out.timezone = String(m.timezone);
    if (m.replyRatePercent != null) out.replyRatePercent = Number(m.replyRatePercent);
    if (m.openRatesPercent != null) out.openRatesPercent = Number(m.openRatesPercent);
    if (m.spamProtectionPercent != null) out.spamProtectionPercent = Number(m.spamProtectionPercent);
    if (m.markImportantPercent != null) out.markImportantPercent = Number(m.markImportantPercent);
    if (m.timeGapMinutes != null) out.timeGapMinutes = Number(m.timeGapMinutes);
    if (m.dailyWarmupLimit != null) out.dailyWarmupLimit = Number(m.dailyWarmupLimit);
    if (m.replyToEnabled != null) out.replyToEnabled = Boolean(m.replyToEnabled);
    if (m.replyToEmails != null) out.replyToEmails = String(m.replyToEmails);
    if (m.customTag != null) out.customTag = String(m.customTag);
    if (m.defaultTag != null) out.defaultTag = String(m.defaultTag);
    if (m.enableCustomTrackingDomain != null) out.enableCustomTrackingDomain = Boolean(m.enableCustomTrackingDomain);
    if (m.trackingDomainOrSubdomain != null) out.trackingDomainOrSubdomain = String(m.trackingDomainOrSubdomain);
    if (m.weekdaysOnly != null) out.weekdaysOnly = Boolean(m.weekdaysOnly);
    if (Array.isArray(m.sendOnDays)) out.sendOnDays = (m.sendOnDays as number[]).slice();
    if (m.deliveryWindowStartMinutes != null) out.deliveryWindowStartMinutes = Number(m.deliveryWindowStartMinutes);
    if (m.deliveryWindowEndMinutes != null) out.deliveryWindowEndMinutes = Number(m.deliveryWindowEndMinutes);
    if (m.readEmulation != null) out.readEmulation = Boolean(m.readEmulation);
    if (m.warmupCustomTrackingDomain != null) out.warmupCustomTrackingDomain = Boolean(m.warmupCustomTrackingDomain);
    return Object.keys(out).length > 0 ? out : null;
  }, [mailbox]);

  const isCombinedLoading =
    isLoadingMailbox || isLoadingSenderMailboxStats || isLoadingMailboxStats;

  if (isCombinedLoading) {
    return <MailboxDetailLoader />;
  }

  if (!mailbox || isErrorMailboxStats) {
    return (
      <StatusScreen
        title="Mailbox not found"
        variant="link-button"
        description="The mailbox you are looking for does not exist."
        icon={<Mail />}
        buttonLink="/mailboxes"
        buttonText="Go to mailboxes"
        wrapperClass="min-h-auto flex justify-center items-center w-full"
        iconWrapperClassName="text-red-500 bg-red-50 dark:bg-red-900/20 dark:text-red-400"
      />
    );
  }

  return (
    <div className="w-full">
      <div className=" space-y-5">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-2">
          <h2 className="page-heading">Mailbox Details</h2>
          <div className="flex items-center gap-2">
            <DatePickerWithRange
              date={date}
              onDateChange={(date) => setDate(date)}
            />
            {/* <SettingsDialog /> */}
          </div>
        </div>

        {/* Header Section */}
        <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4 pb-6 border-b">
          <div className="flex flex-col sm:flex-row items-center gap-2">
            <div className="flex items-center gap-3 bg-background rounded-lg border px-4 py-2">
              <div className="size-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-sm font-medium">
                {mailbox?.email || sender}
              </span>
            </div>
            <span>
              {sender ? (
                <Select
                  value={selectedSenderEsp || undefined}
                  onValueChange={(value) => setSelectedSenderEsp(value)}
                  disabled={isLoadingSenderEspTypes || !uniqueSenderEspTypes?.length}
                >
                  <SelectTrigger className="flex items-center gap-3 w-[200px] sm:min-w-[220px] bg-background rounded-lg border px-4 py-2 h-auto">
                    <SelectValue placeholder={isLoadingSenderEspTypes ? "Loading ESPs..." : "Select ESP"} />
                  </SelectTrigger>
                  <SelectContent>
                    {uniqueSenderEspTypes?.map((esp) => {
                      const espStr = String(esp);
                      const iconMapping =
                        authProviderIcons[espStr] ||
                        authProviderIcons[espStr.toLowerCase()] ||
                        authProviderIcons["smtp"];
                      return (
                        <SelectItem key={esp} value={esp}>
                          <div className="flex items-center gap-2">
                            <span>{iconMapping?.icon}</span>
                            <span>{iconMapping?.tooltip || esp}</span>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-3 bg-background rounded-lg border px-4 py-2">
                      {(() => {
                        const espTypeOrAuthProvider = (mailbox as any)?.espType || mailbox?.authProvider || "smtp";
                        const espTypeStr = String(espTypeOrAuthProvider);
                        const iconMapping = authProviderIcons[espTypeStr] || authProviderIcons[espTypeStr.toLowerCase()] || authProviderIcons["smtp"];
                        return (
                          <>
                            <span>{iconMapping?.icon}</span>
                            <span className="text-sm font-light">{iconMapping?.tooltip || "Private SMTP"}</span>
                          </>
                        );
                      })()}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    {(() => {
                      const espTypeOrAuthProvider = (mailbox as any)?.espType || mailbox?.authProvider || "smtp";
                      const espTypeStr = String(espTypeOrAuthProvider);
                      const iconMapping = authProviderIcons[espTypeStr] || authProviderIcons[espTypeStr.toLowerCase()] || authProviderIcons["smtp"];
                      return iconMapping?.tooltip || "Private SMTP";
                    })()}
                  </TooltipContent>
                </Tooltip>
              )}
            </span>
            <div className="flex items-center gap-2 bg-background rounded-lg border px-4 py-2">
              <span className="text-sm text-muted-foreground">
                Created:{" "}
                {mailbox?.createdAt
                  ? formatDate(mailbox.createdAt)
                  : formatDate(new Date().toLocaleDateString())}
              </span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 justify-between xl:justify-end">
            {mailbox && !sender && (
              <AssignSubscriptionSelect
                mailboxId={mailboxid}
                mailboxEmail={mailbox?.email}
                subscriptionId={mailbox?.subscriptionId}
                planId={mailbox?.planId}
                addonPlanId={mailbox?.addonPlan}
                pageType="details"
                className="h-9 text-sm sm:min-w-[250px] w-full sm:w-fit"
              />
            )}

            <div className="flex items-center justify-between sm:justify-end gap-2 w-full sm:w-fit">
              {hasPermission("mailboxes:edit") && (
                <div className="flex items-center gap-3 bg-background rounded-lg border px-4 py-2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium">Warm up</span>
                        <Switch
                          checked={mailbox?.isWarmupEnabled}
                          disabled={!hasPermission("mailboxes:edit")}
                          onCheckedChange={handleMailboxWarmupToggle}
                        />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Toggle warm-up status</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              )}
              {!sender && hasPermission("mailboxes:edit") && (
                <div className="flex items-center gap-2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        disabled={
                          !hasPermission("mailboxes:edit") || updatingMailbox
                        }
                        size="icon"
                        className="h-10 w-10"
                        onClick={() => {
                          updateMailbox({
                            id: mailboxid,
                            mailboxData: {
                              isActive: !mailbox?.isActive,
                            },
                          });

                          const eventName = mailbox?.isActive
                            ? "mailbox_disconnected"
                            : "mailbox_connected";

                          window.cioanalytics?.track(eventName, {
                            email: mailbox?.email,
                          });
                        }}
                      >
                        {updatingMailbox ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : mailbox?.isActive ? (
                          <PlugsConnected className="h-4 w-4 text-green-500" />
                        ) : (
                          <Plugs className="h-4 w-4 text-red-500" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {mailbox?.isActive ? (
                        <p>Mailbox is connected</p>
                      ) : (
                        <p>Mailbox is not connected</p>
                      )}
                      {/* <p>Reconnect mailbox</p> */}
                    </TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-10 w-10"
                        onClick={() => {
                          if (!mailbox?.planId && !mailbox?.addonPlan) {
                            toast.error("Please assign a plan to this mailbox first.");
                            return;
                          }
                          setShowB2BSettings(true);
                        }}
                      >
                        <Settings className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>B2B warmup settings (ESP mix, timezone, strategy)</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              )}
            </div>
          </div>
        </div>

        <SettingsDialog
          mailbox={mailbox}
          open={showSettings}
          onOpenChange={setShowSettings}
        />

        {/* B2B Warmup Settings: update ESP distribution, timezone, strategy, etc. When opened from warmup switch (enableWarmupAfterSave), save also enables warmup. */}
        {!sender && mailbox && (
          <WarmupB2BSettingsDialog
            open={showB2BSettings}
            onOpenChange={(open) => {
              setShowB2BSettings(open);
              if (!open) setEnableWarmupAfterSave(false);
            }}
            mailboxId={mailboxid}
            mailboxEmail={mailbox?.email}
            mailboxFirstName={mailbox?.firstName}
            mailboxLastName={mailbox?.lastName}
            planAssigned={!!(mailbox?.planId || mailbox?.addonPlan)}
            planMaxDailyLimit={mailbox?.customWarmupPlan?.maxDailyLimit}
            onSaveAndEnableWarmup={
              enableWarmupAfterSave ? handleB2BSaveAndEnableWarmup : undefined
            }
            espBreakdown={(mailbox as any)?.warmupConfig?.espBreakdown}
            mailboxWarmupOverrides={mailboxWarmupOverrides}
            onMailboxNameChange={async (firstName, lastName) => {
              await updateMailboxAsync({
                id: mailboxid,
                mailboxData: { firstName, lastName },
              });
            }}
          />
        )}

        {/* Settings Grid */}
        {sender ? (
          <div className="space-y-4">
            {/* Row 1: Language, Target Audience, Seed Assigned */}
            <div className="grid gap-4 md:grid-cols-3">
              {/* Language Card */}
              <div className="group relative">
                <div className="relative bg-background border rounded-lg p-4 space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Globe className="h-5 w-5 text-primary" />
                    </div>
                    <h3 className="font-semibold">Language</h3>
                  </div>
                  <Select defaultValue="english">
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select language" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="english">English</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Target Audience Card */}
              <div className="group relative">
                <div className="relative bg-background border rounded-lg p-4 space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Users2 className="h-5 w-5 text-primary" />
                    </div>
                    <h3 className="font-semibold">Target Audience</h3>
                  </div>
                  <div className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm">
                    <span className="text-foreground">B2C</span>
                  </div>
                </div>
              </div>

              {/* Seed Assigned Card */}
              <div className="group relative">
                <div className="relative bg-background border rounded-lg p-4 space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-500/10 rounded-lg">
                      <Users2 className="h-5 w-5 text-purple-600" />
                    </div>
                    <h3 className="font-semibold">Seed Assigned</h3>
                  </div>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button type="button" className="flex h-8 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1">
                        <span className="flex-1 truncate text-left">
                        {selectedSeeds.length === 0 ? (
                          "Select seed types"
                          ) : (
                            selectedSeeds.map(seed => 
                              seed === "personal-gmail" ? "Gmail" :
                              seed === "personal-outlook" ? "Outlook" :
                              seed === "personal-yahoo" ? "Yahoo" :
                              seed === "Google-Workspace" ? "Google-Workspace" :
                              seed === "Microsoft365" ? "Microsoft365" :
                              seed === "personal-zoho" ? "Zoho" :
                              "Unknown"
                            ).join(", ")
                          )}
                        </span>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="h-4 w-4 opacity-50"
                        >
                          <path d="m6 9 6 6 6-6" />
                        </svg>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-56 p-0">
                      <div className="p-2">
                        <div 
                          className="flex items-center space-x-2 rounded-sm px-2 py-1.5 hover:bg-accent cursor-pointer"
                          onClick={() => handleSeedChange("personal-gmail")}
                        >
                          <Checkbox 
                            checked={selectedSeeds.includes("personal-gmail")}
                          />
                          <label className="text-sm font-normal cursor-pointer">
                            Personal Gmail
                          </label>
                        </div>
                        <div 
                          className="flex items-center space-x-2 rounded-sm px-2 py-1.5 hover:bg-accent cursor-pointer"
                          onClick={() => handleSeedChange("personal-outlook")}
                        >
                          <Checkbox 
                            checked={selectedSeeds.includes("personal-outlook")}
                          />
                          <label className="text-sm font-normal cursor-pointer">
                            Personal Outlook
                          </label>
                        </div>
                        <div 
                          className="flex items-center space-x-2 rounded-sm px-2 py-1.5 hover:bg-accent cursor-pointer"
                          onClick={() => handleSeedChange("personal-yahoo")}
                        >
                          <Checkbox 
                            checked={selectedSeeds.includes("personal-yahoo")}
                          />
                          <label className="text-sm font-normal cursor-pointer">
                            Personal Yahoo
                          </label>
                        </div>
                        <div 
                          className="flex items-center space-x-2 rounded-sm px-2 py-1.5 hover:bg-accent cursor-pointer"
                          onClick={() => handleSeedChange("Google-Workspace")}
                        >
                          <Checkbox 
                            checked={selectedSeeds.includes("Google-Workspace")}
                          />
                          <label className="text-sm font-normal cursor-pointer">
                            Google Workspace
                          </label>
                        </div>
                        <div 
                          className="flex items-center space-x-2 rounded-sm px-2 py-1.5 hover:bg-accent cursor-pointer"
                          onClick={() => handleSeedChange("Microsoft365")}
                        >
                          <Checkbox 
                            checked={selectedSeeds.includes("Microsoft365")}
                          />
                          <label className="text-sm font-normal cursor-pointer">
                            Microsoft 365
                          </label>
                        </div>
                        <div 
                          className="flex items-center space-x-2 rounded-sm px-2 py-1.5 hover:bg-accent cursor-pointer"
                          onClick={() => handleSeedChange("personal-zoho")}
                        >
                          <Checkbox 
                            checked={selectedSeeds.includes("personal-zoho")}
                          />
                          <label className="text-sm font-normal cursor-pointer">
                            Personal Zoho
                          </label>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>

            {/* Row 2: Gmail - Received, Open, Clicked */}
            {selectedSeeds.includes("personal-gmail") && (
            <div className="grid gap-4 md:grid-cols-3">
              {/* Received on Gmail Today */}
              <div className="group relative">
                <div className="relative bg-background border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground font-medium">
                        Received on Gmail Today
                      </p>
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-bold">{mailboxStats?.dailyStats?.[0]?.date == formatDateLocal(new Date()) ? mailboxStats?.dailyStats?.[0]?.gmailOpens ?? 0 : 0}</span>
                        <span className="text-sm text-muted-foreground">emails</span>
                      </div>
                    </div>
                    <div className="p-3 bg-red-500/10 rounded-lg">
                      <img 
                        src="/providers-logos/gmail.png" 
                        alt="Gmail" 
                        className="h-6 w-6"
                      />
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      Total Gmail received:{" "}
                      <span className="font-medium">{mailbox.totalgmailrecived}</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Open on Gmail Today */}
              <div className="group relative">
                <div className="relative bg-background border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground font-medium">
                        Open on Gmail Today
                      </p>
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-bold">{mailboxStats?.dailyStats?.[0]?.date == formatDateLocal(new Date()) ? mailboxStats?.dailyStats?.[0]?.gmailOpens ?? 0 : 0}</span>
                        <span className="text-sm text-muted-foreground">emails</span>
                      </div>
                    </div>
                    <div className="p-3 bg-green-500/10 rounded-lg">
                      <img 
                        src="/providers-logos/gmail.png" 
                        alt="Gmail" 
                        className="h-6 w-6"
                      />
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      Total Gmail opened:{" "}
                      <span className="font-medium">{mailbox.totalgmailrecived}</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Clicked in Gmail Today */}
              <div className="group relative">
                <div className="relative bg-background border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground font-medium">
                        Clicked in Gmail Today
                      </p>
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-bold">{mailbox.todayClicksGmail}</span>
                        <span className="text-sm text-muted-foreground">emails</span>
                      </div>
                    </div>
                    <div className="p-3 bg-orange-500/10 rounded-lg">
                      <img 
                        src="/providers-logos/gmail.png" 
                        alt="Gmail" 
                        className="h-6 w-6"
                      />
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      Total Gmail clicked:{" "}
                      <span className="font-medium">{mailbox.totalClicksGmail}</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
            )}

            {/* Row 3: Outlook - Received, Open, Clicked */}
            {selectedSeeds.includes("personal-outlook") && (
            <div className="grid gap-4 md:grid-cols-3">
              {/* Received on Outlook Today */}
              <div className="group relative">
                <div className="relative bg-background border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground font-medium">
                        Received on Outlook Today
                      </p>
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-bold">{mailboxStats?.dailyStats?.[0]?.date == formatDateLocal(new Date()) ? mailboxStats?.dailyStats?.[0]?.outlookOpens ?? 0 : 0}</span>
                        <span className="text-sm text-muted-foreground">emails</span>
                      </div>
                    </div>
                    <div className="p-3 bg-blue-600/10 rounded-lg">
                      <img 
                        src="/providers-logos/outlook.png" 
                        alt="Outlook" 
                        className="h-6 w-6"
                      />
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      Total Outlook received:{" "}
                      <span className="font-medium">{mailbox.totaloutlookrecived}</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Open on Outlook Today */}
              <div className="group relative">
                <div className="relative bg-background border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground font-medium">
                        Open on Outlook Today
                      </p>
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-bold">{mailboxStats?.dailyStats?.[0]?.date == formatDateLocal(new Date()) ? mailboxStats?.dailyStats?.[0]?.outlookOpens ?? 0 : 0}</span>
                        <span className="text-sm text-muted-foreground">emails</span>
                      </div>
                    </div>
                    <div className="p-3 bg-green-600/10 rounded-lg">
                      <img 
                        src="/providers-logos/outlook.png" 
                        alt="Outlook" 
                        className="h-6 w-6"
                      />
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      Total Outlook opened:{" "}
                      <span className="font-medium">{mailbox.totaloutlookrecived}</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Clicked in Outlook Today */}
              <div className="group relative">
                <div className="relative bg-background border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground font-medium">
                        Clicked in Outlook Today
                      </p>
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-bold">{mailbox.todayClicksOutlook}</span>
                        <span className="text-sm text-muted-foreground">emails</span>
                      </div>
                    </div>
                    <div className="p-3 bg-orange-600/10 rounded-lg">
                      <img 
                        src="/providers-logos/outlook.png" 
                        alt="Outlook" 
                        className="h-6 w-6"
                      />
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      Total Outlook clicked:{" "}
                      <span className="font-medium">{mailbox.totalClicksOutlook}</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
            )}

            {/* Row 4: Yahoo - Received, Open, Clicked */}
            {selectedSeeds.includes("personal-yahoo") && (
            <div className="grid gap-4 md:grid-cols-3">
              {/* Received on Yahoo Today */}
              <div className="group relative">
                <div className="relative bg-background border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground font-medium">
                        Received on Yahoo Today
                      </p>
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-bold">0</span>
                        <span className="text-sm text-muted-foreground">emails</span>
                      </div>
                    </div>
                    <div className="p-3 bg-purple-500/10 rounded-lg">
                      <img 
                        src="/providers-logos/yahoo.png" 
                        alt="Yahoo" 
                        className="h-6 w-6"
                      />
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      Total Yahoo received:{" "}
                      <span className="font-medium">0</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Open on Yahoo Today */}
              <div className="group relative">
                <div className="relative bg-background border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground font-medium">
                        Open on Yahoo Today
                      </p>
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-bold">0</span>
                        <span className="text-sm text-muted-foreground">emails</span>
                      </div>
                    </div>
                    <div className="p-3 bg-green-700/10 rounded-lg">
                      <img 
                        src="/providers-logos/yahoo.png" 
                        alt="Yahoo" 
                        className="h-6 w-6"
                      />
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      Total Yahoo opened:{" "}
                      <span className="font-medium">0</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Clicked in Yahoo Today */}
              <div className="group relative">
                <div className="relative bg-background border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground font-medium">
                        Clicked in Yahoo Today
                      </p>
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-bold">0</span>
                        <span className="text-sm text-muted-foreground">emails</span>
                      </div>
                    </div>
                    <div className="p-3 bg-orange-700/10 rounded-lg">
                      <img 
                        src="/providers-logos/yahoo.png" 
                        alt="Yahoo" 
                        className="h-6 w-6"
                      />
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      Total Yahoo clicked:{" "}
                      <span className="font-medium">0</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
            )}

            {/* Row 5: Google Workspace - Received, Open, Clicked */}
            {selectedSeeds.includes("Google-Workspace") && (
            <div className="grid gap-4 md:grid-cols-3">
              {/* Received on Google Workspace Today */}
              <div className="group relative">
                <div className="relative bg-background border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground font-medium">
                        Received on Google Workspace Today
                      </p>
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-bold">{mailboxStats?.dailyStats?.[0]?.date == formatDateLocal(new Date()) ? mailboxStats?.dailyStats?.[0]?.workspaceOpens ?? 0 : 0}</span>
                        <span className="text-sm text-muted-foreground">emails</span>
                      </div>
                    </div>
                    <div className="p-3 bg-red-500/10 rounded-lg">
                      <img 
                        src="/providers-logos/google-workspace.png" 
                        alt="Google Workspace" 
                        className="h-6 w-6"
                      />
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      Total Google Workspace received:{" "}
                      <span className="font-medium">{mailbox.totalworkspacerecived}</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Open on Google Workspace Today */}
              <div className="group relative">
                <div className="relative bg-background border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground font-medium">
                        Open on Google Workspace Today
                      </p>
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-bold">{mailboxStats?.dailyStats?.[0]?.date == formatDateLocal(new Date()) ? mailboxStats?.dailyStats?.[0]?.workspaceOpens ?? 0 : 0}</span>
                        <span className="text-sm text-muted-foreground">emails</span>
                      </div>
                    </div>
                    <div className="p-3 bg-green-500/10 rounded-lg">
                      <img 
                        src="/providers-logos/google-workspace.png" 
                        alt="Google Workspace" 
                        className="h-6 w-6"
                      />
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      Total Google Workspace opened:{" "}
                      <span className="font-medium">{mailbox.totalworkspacerecived}</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Clicked in Google Workspace Today */}
              <div className="group relative">
                <div className="relative bg-background border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground font-medium">
                        Clicked in Google Workspace Today
                      </p>
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-bold">{mailbox.todayClicksWorkspace}</span>
                        <span className="text-sm text-muted-foreground">emails</span>
                      </div>
                    </div>
                    <div className="p-3 bg-orange-500/10 rounded-lg">
                      <img 
                        src="/providers-logos/google-workspace.png" 
                        alt="Google Workspace" 
                        className="h-6 w-6"
                      />
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      Total Google Workspace clicked:{" "}
                      <span className="font-medium">{mailbox.totalClicksWorkspace}</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
            )}

            {/* Row 6: Microsoft 365 - Received, Open, Clicked */}
            {selectedSeeds.includes("Microsoft365") && (
            <div className="grid gap-4 md:grid-cols-3">
              {/* Received on Microsoft 365 Today */}
              <div className="group relative">
                <div className="relative bg-background border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground font-medium">
                        Received on Microsoft 365 Today
                      </p>
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-bold">{mailboxStats?.dailyStats?.[0]?.date == formatDateLocal(new Date()) ? mailboxStats?.dailyStats?.[0]?.microsoftOpens ?? 0 : 0}</span>
                        <span className="text-sm text-muted-foreground">emails</span>
                      </div>
                    </div>
                    <div className="p-3 bg-blue-600/10 rounded-lg">
                      <img 
                        src="/providers-logos/microsoft-365.png" 
                        alt="Microsoft 365" 
                        className="h-6 w-6"
                      />
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      Total Microsoft 365 received:{" "}
                      <span className="font-medium">{mailbox.totalmicrosoftrecived}</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Open on Microsoft 365 Today */}
              <div className="group relative">
                <div className="relative bg-background border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground font-medium">
                        Open on Microsoft 365 Today
                      </p>
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-bold">{mailboxStats?.dailyStats?.[0]?.date == formatDateLocal(new Date()) ? mailboxStats?.dailyStats?.[0]?.microsoftOpens ?? 0 : 0}</span>
                        <span className="text-sm text-muted-foreground">emails</span>
                      </div>
                    </div>
                    <div className="p-3 bg-green-600/10 rounded-lg">
                      <img 
                        src="/providers-logos/microsoft-365.png" 
                        alt="Microsoft 365" 
                        className="h-6 w-6"
                      />
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      Total Microsoft 365 opened:{" "}
                      <span className="font-medium">{mailbox.totalmicrosoftrecived}</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Clicked in Microsoft 365 Today */}
              <div className="group relative">
                <div className="relative bg-background border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground font-medium">
                        Clicked in Microsoft 365 Today
                      </p>
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-bold">{mailbox.todayClicksMicrosoft}</span>
                        <span className="text-sm text-muted-foreground">emails</span>
                      </div>
                    </div>
                    <div className="p-3 bg-orange-600/10 rounded-lg">
                      <img 
                        src="/providers-logos/microsoft-365.png" 
                        alt="Microsoft 365" 
                        className="h-6 w-6"
                      />
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      Total Microsoft 365 clicked:{" "}
                      <span className="font-medium">{mailbox.totalClicksMicrosoft}</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
            )}

            {/* Row 7: Zoho - Received, Open, Clicked */}
            {selectedSeeds.includes("personal-zoho") && (
            <div className="grid gap-4 md:grid-cols-3">
              {/* Received on Zoho Today */}
              <div className="group relative">
                <div className="relative bg-background border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground font-medium">
                        Received on Zoho Today
                      </p>
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-bold">{mailboxStats?.dailyStats?.[0]?.date == formatDateLocal(new Date()) ? (mailboxStats?.dailyStats?.[0] as any)?.zohoOpens ?? 0 : 0}</span>
                        <span className="text-sm text-muted-foreground">emails</span>
                      </div>
                    </div>
                    <div className="p-3 bg-purple-500/10 rounded-lg">
                      <img 
                        src="/providers-logos/zoho.png" 
                        alt="Zoho" 
                        className="h-6 w-6"
                      />
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      Total Zoho received:{" "}
                      <span className="font-medium">{(mailbox as any).totalzohorecived || 0}</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Open on Zoho Today */}
              <div className="group relative">
                <div className="relative bg-background border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground font-medium">
                        Open on Zoho Today
                      </p>
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-bold">{mailboxStats?.dailyStats?.[0]?.date == formatDateLocal(new Date()) ? (mailboxStats?.dailyStats?.[0] as any)?.zohoOpens ?? 0 : 0}</span>
                        <span className="text-sm text-muted-foreground">emails</span>
                      </div>
                    </div>
                    <div className="p-3 bg-green-700/10 rounded-lg">
                      <img 
                        src="/providers-logos/zoho.png" 
                        alt="Zoho" 
                        className="h-6 w-6"
                      />
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      Total Zoho opened:{" "}
                      <span className="font-medium">{mailbox.totalzohorecived || 0}</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Clicked in Zoho Today */}
              <div className="group relative">
                <div className="relative bg-background border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground font-medium">
                        Clicked in Zoho Today
                      </p>
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-bold">{(mailbox as any).todayClicksZoho || 0}</span>
                        <span className="text-sm text-muted-foreground">emails</span>
                      </div>
                    </div>
                    <div className="p-3 bg-orange-700/10 rounded-lg">
                      <img 
                        src="/providers-logos/zoho.png" 
                        alt="Zoho" 
                        className="h-6 w-6"
                      />
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      Total Zoho clicked:{" "}
                      <span className="font-medium">{(mailbox as any).totalClicksZoho || 0}</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
            )}

          </div>
        ) : (
          <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {/* Language Card */}
          <div className="group relative">
            <div className="relative bg-background border rounded-lg p-4 space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Globe className="h-5 w-5 text-primary" />
                </div>
                <h3 className="font-semibold">Language</h3>
              </div>
              <Select defaultValue="english">
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select language" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="english">English</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Template Card - Only show if no sender */}
          {!sender && hasPermission("mailboxes:edit") && (
            <div className="group relative">
              <div className="relative bg-background border rounded-lg p-4 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <FileText className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-semibold">Template</h3>
                </div>
                <ScheduleTemplates
                  mailboxId={mailboxid}
                  mailboxEmail={mailbox?.email}
                />
              </div>
            </div>
          )}

          {/* Provider Card */}
          {hasPermission("mailboxes:edit") && (
            <div className="group relative">
              <div className="relative h-full bg-background border rounded-lg p-4 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Users2 className="h-5 w-5 text-primary" />
                  </div>
                  <p className="text-sm text-muted-foreground font-medium">
                    Target Audience
                  </p>{" "}
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <HelpCircle className="h-4 w-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Current target audience type</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Select
                  value={mailbox?.targetAudience || undefined}
                  disabled={!sender}
                  onValueChange={(value) => {
                    if (sender) {
                      console.log('Target audience changed to:', value);
                    }
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select audience" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="b2b">B2B</SelectItem>
                    <SelectItem value="b2c">B2C</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* ESP Distribution Card - inline icon row with tooltips (no dialog) */}
          {!sender && (() => {
            const breakdown = mailbox?.warmupConfig?.espBreakdown;
            const useBreakdown = breakdown && typeof breakdown === "object";
            const keys = useBreakdown ? ESP_BREAKDOWN_KEYS : ESP_DISTRIBUTION_KEYS;
            let total = 0;
            return (
              <div className="group relative">
                <div className="relative bg-background border rounded-lg p-4 space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <PieChart className="h-5 w-5 text-primary" />
                    </div>
                    <h3 className="font-semibold">ESP Distribution</h3>
                  </div>
                  <TooltipProvider>
                    <div className="flex flex-wrap gap-2">
                      {keys.map(({ key, label, iconKey }) => {
                        const num = useBreakdown
                          ? (typeof breakdown?.[key] === "number" ? breakdown[key] as number : 0)
                          : (typeof b2bSettings?.[key as keyof typeof b2bSettings] === "number" ? b2bSettings[key as keyof typeof b2bSettings] as number : 0);
                        total += num;
                        const icon = authProviderIcons[iconKey]?.icon;
                        return (
                          <Tooltip key={key}>
                            <TooltipTrigger asChild>
                              <div className="flex items-center justify-center w-11 h-11 rounded-lg bg-muted/60 border border-border hover:bg-muted hover:border-primary/30 transition-colors [&>img]:!size-5 [&>svg]:size-5 cursor-default">
                                {icon ?? null}
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="tabular-nums">
                              {label}: {num}%
                            </TooltipContent>
                          </Tooltip>
                        );
                      })}
                    </div>
                  </TooltipProvider>
                 
                </div>
              </div>
            );
          })()}

          {/* Timezone Card - non-sender (same card style, h-full to match row height) */}
          {!sender && (() => {
            const sendOnDays = (mailbox as any)?.sendOnDays ?? b2bSettings?.sendOnDays;
            const startMin = (mailbox as any)?.deliveryWindowStartMinutes ?? b2bSettings?.deliveryWindowStartMinutes;
            const endMin = (mailbox as any)?.deliveryWindowEndMinutes ?? b2bSettings?.deliveryWindowEndMinutes;
            const hasWindow = startMin != null && endMin != null;
            const timeRangeStr = hasWindow
              ? `${formatMinutesToTime(Number(startMin))} - ${formatMinutesToTime(Number(endMin))}`
              : "—";
            const selectedSet = new Set(Array.isArray(sendOnDays) ? sendOnDays : []);

            return (
              <div className="group relative h-full">
                <div className="relative h-full bg-background border rounded-lg p-4 space-y-4 flex flex-col">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <Clock className="h-5 w-5 text-primary" />
                      </div>
                      <h3 className="font-semibold">Timezone</h3>
                    </div>
                    <p className="text-sm text-muted-foreground text-right truncate max-w-[60%]">
                      {mailbox?.timezone ?? b2bSettings?.timezone ?? "—"}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    <TooltipProvider>
                      {TIMEZONE_DAY_ORDER.map(({ dayNum, letter, name }) => {
                        const selected = selectedSet.has(dayNum);
                        return (
                          <Tooltip key={dayNum}>
                            <TooltipTrigger asChild>
                              <div
                                className={`
                                  flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-sm font-medium transition-colors
                                  ${selected
                                    ? "bg-blue-400 text-primary-foreground"
                                    : "bg-muted text-muted-foreground"
                                  }
                                `}
                              >
                                {letter}
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="text-center">
                              <p className="font-medium">{name}</p>
                              <p className="text-xs ">{timeRangeStr}</p>
                            </TooltipContent>
                          </Tooltip>
                        );
                      })}
                    </TooltipProvider>
                  </div>
                  
                </div>
              </div>
            );
          })()}

{!sender && (() => {
            const plan = mailbox?.customWarmupPlan;
            const strategy = plan?.warmupStrategy;
            const strategyConfig = getStrategyConfig(strategy);
            const StrategyIcon = strategyConfig.icon;
            const showStrategyPill = mailbox?.isWarmupEnabled && strategy && strategyConfig.short !== "—";
            const tooltipLines = mailbox ? buildTooltipText(mailbox) : "";
            const daysToMax = mailbox?.daysToMaxLimit;
            const limitsTooltipParts = [
              ...(tooltipLines ? tooltipLines.split(" · ") : []),
              strategy === "progressive" && daysToMax != null ? `~${daysToMax} days to max` : null,
            ].filter(Boolean) as string[];

            return (
            <div className="group relative">
              <div className="relative bg-background border rounded-lg p-4">
                <div className="flex items-center justify-between gap-2 mb-4">
                  <div className="flex items-center gap-2 min-w-0">
                    <p className="text-sm text-muted-foreground font-medium">
                      Mailbox Temperature
                    </p>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="shrink-0">
                            <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Current warmup limit and strategy</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  {showStrategyPill && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div
                            className={cn(
                              "flex items-center gap-1.5 shrink-0 rounded-md border px-2 py-0.5 text-xs font-medium cursor-default",
                              strategyConfig.className
                            )}
                          >
                            <span className={cn("size-1.5 rounded-full shrink-0", strategyConfig.dotClassName)} />
                            <StrategyIcon className="size-3 shrink-0" />
                            <span className="truncate">{strategyConfig.short}</span>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="left" className="max-w-[280px]">
                          <p className="text-xs whitespace-pre-line">
                            {limitsTooltipParts.length > 0 ? limitsTooltipParts.join("\n") : "Strategy and limits"}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold">
                    {mailbox?.currentDailyLimit ?? mailbox?.customWarmupPlan?.currentCounter ?? "—"}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    /day
                  </span>
                </div>
                <div className="space-y-2">
                  {(() => {
                    const plan = mailbox?.customWarmupPlan;
                    const max = plan?.maxDailyLimit ?? plan?.progressiveMaxLimit ?? 0;
                    const current = mailbox?.currentDailyLimit ?? plan?.currentCounter ?? 0;
                    const showBar = plan?.warmupStrategy === "progressive" && max > 0;
                    return (
                      <>
                        {showBar && (
                          <>
                            <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
                              <div
                                className="h-full bg-gradient-to-r from-orange-500 to-red-500 rounded-full transition-all duration-500"
                                style={{
                                  width: `${Math.min((Number(current) / max) * 100, 100)}%`,
                                }}
                              />
                            </div>
                            <div className="flex justify-between text-xs text-muted-foreground">
                              <span>Progress</span>
                              <span>~{mailbox?.daysToMaxLimit ?? "—"} days</span>
                            </div>
                          </>
                        )}
                        {!showBar && plan?.warmupStrategy === "random" && (
                          <p className="text-xs text-muted-foreground">
                            Random: {plan.randomMinLimit ?? 0}–{plan.randomMaxLimit ?? 0} emails/day
                          </p>
                        )}
                        {!showBar && plan?.warmupStrategy === "flat" && (
                          <p className="text-xs text-muted-foreground">
                            Flat: {plan.flatConstantEmails ?? 0} emails/day
                          </p>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
            );
          })()}

          {/* Custom Domain Card - show tracking domain if set */}
          {!sender && (
            <div className="group relative h-full">
              <div className="relative h-full bg-background border rounded-lg p-4 flex flex-col">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm text-muted-foreground font-medium">
                    Custom Domain
                  </p>
                </div>
                <p className="text-xl font-bold truncate mt-1">
                  { b2bSettings?.trackingDomainOrSubdomain == 'inst.yourdomain.com' ? "-" : b2bSettings?.trackingDomainOrSubdomain ?? "—"}
                </p>
                <div className="mt-4 pt-4 border-t flex-1 min-h-[2rem]">
                  <p className="text-sm text-muted-foreground">
                    Used for warmup tracking links
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Sent Today Card */}
          <div className="group relative">
            <div className="relative bg-background border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground font-medium">Sent Today</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold">
                      {!sender
                        ? mailbox?.emailsSentToday
                        : senderMailboxStats?.today?.totalEmails?.toLocaleString() ??
                          "0"}
                    </span>
                    <span className="text-sm text-muted-foreground">emails</span>
                  </div>
                </div>
                <div className="p-3 bg-blue-500/10 rounded-lg">
                  <Mail className="h-6 w-6 text-blue-500" />
                </div>
              </div>
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm text-muted-foreground">
                  Total sent:{" "}
                  <span className="font-medium">
                    {!sender
                      ? mailbox?.totalEmailsSent
                      : mailboxStats?.summary.totalReceived}
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Received Today Card */}
          <div className="group relative">
            <div className="relative bg-background border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground font-medium">Received Today</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold">
                      {mailboxStats?.summary.todayrecived ?? "0"}
                    </span>
                    <span className="text-sm text-muted-foreground">emails</span>
                  </div>
                </div>
                <div className="p-3 bg-green-500/10 rounded-lg">
                  <MailCheck className="h-6 w-6 text-green-500" />
                </div>
              </div>
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm text-muted-foreground">
                  Total received:{" "}
                  <span className="font-medium">
                    {mailboxStats?.summary.totalReceived}
                  </span>
                </p>
              </div>
            </div>
          </div>

         
          </div>

          <div className="grid gap-4 md:grid-cols-2 mt-4">
            <div className="group relative">
              <div className="relative bg-background border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground font-medium">
                      Today's Inbox
                    </p>
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl font-bold">
                        {mailboxStats?.summary.todayinbox ?? 0}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        emails
                      </span>
                    </div>
                  </div>
                  <div className="p-3 bg-blue-500/10 rounded-lg">
                    <Mail className="h-6 w-6 text-blue-500" />
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t">
                  <p className="text-sm text-muted-foreground">
                    Total inbox:{" "}
                    <span className="font-medium">
                      {mailboxStats?.summary.totalInbox}
                    </span>
                  </p>
                </div>
              </div>
            </div>

            {/* Today's Spam Card */}
            <div className="group relative">
              <div className="relative bg-background border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground font-medium">
                      Today's Spam
                    </p>
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl font-bold">
                        {mailboxStats?.summary.todayspam ?? 0}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        emails
                      </span>
                    </div>
                  </div>
                  <div className="p-3 bg-orange-500/10 rounded-lg">
                    <Mail className="h-6 w-6 text-orange-500" />
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t">
                  <p className="text-sm text-muted-foreground">
                    Total spam:{" "}
                    <span className="font-medium">
                      {mailboxStats?.summary.totalSpam}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>

        <div className="grid gap-4 md:grid-cols-2 mt-4">
          <div className="group relative">
            <div className="relative  bg-background border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground font-medium">
                    Today's Google Workspace Replies
                  </p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold">
                      {todayGmailReplies
                        ? (
                            todayGmailReplies
                          ).toLocaleString()
                        : "0"}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      emails
                    </span>
                  </div>
                </div>
                <div className="p-3 bg-blue-500/10 rounded-lg">
                  {authProviderIcons['google']?.icon}
                </div>
              </div>
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm text-muted-foreground">
                  Total Google Workspace Replies:{" "}
                  <span className="font-medium">
                    {mailboxStats?.summary.totalGmailReplies || 0}
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Today's Spam Card */}
          <div className="group relative">
            <div className="relative bg-background border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground font-medium">
                    Today's Microsoft 365 Replies
                  </p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold">
                      {todayOutlookReplies?.toLocaleString() ??
                        "0"}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      emails
                    </span>
                  </div>
                </div>
                <div className="p-3 bg-orange-500/10 rounded-lg">
                {authProviderIcons['microsoft']?.icon}
                </div>
              </div>
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm text-muted-foreground">
                  Total Microsoft 365 Replies:{" "}
                  <span className="font-medium">
                    {mailboxStats?.summary.totalOutlookReplies}
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* <div className="group relative">
            <div className="relative bg-background border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground font-medium">
                    Today's Yahoo Replies
                  </p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold">
                      {mailbox?.todayYahooReplies?.toLocaleString() ??
                        "0"}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      emails
                    </span>
                  </div>
                </div>
                <div className="p-3 bg-orange-500/10 rounded-lg">
                {authProviderIcons['yahoo']?.icon}
                </div>
              </div>
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm text-muted-foreground">
                  Total Yahoo Replies:{" "}
                  <span className="font-medium">
                    {mailbox?.totalYahooReplies ?? 0}
                  </span>
                </p>
              </div>
            </div>
          </div> */}
        </div>
          </>
        )}

         {/* Google Workspace Email Placement */}
         {(!sender || selectedSeeds.includes("Google-Workspace")) && (
         <div className="space-y-2">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            Email Placement - Google Workspace 
          </h2>

          <div className="bg-background border rounded-lg p-4 space-y-4">
            {workspaceStats.map((stat) => (
              <div key={stat.name} className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="font-medium">{stat.name}</span>
                  <span className="text-muted-foreground">
                    {stat.percentage.toFixed(1)}%
                  </span>
                </div>
                <div className="relative h-2 dark:bg-input bg-muted rounded-full overflow-hidden">
                  <div
                    className={`absolute top-0 left-0 h-full rounded-full ${
                      stat.name === "Spam" ? "bg-red-500" : "bg-primary"
                    }`}
                    style={{ width: `${stat.percentage}%` }}
                  />
                </div>
                <div className="text-right">
                  <span className="text-sm text-muted-foreground">
                    Count: {stat.score.toLocaleString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
         )}

       

        {/* Microsoft Workspace Email Placement */}
        {(!sender || selectedSeeds.includes("Microsoft365")) && (
        <div className="space-y-2">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            Email Placement - Microsoft 365
          </h2>

          <div className="bg-background border rounded-lg p-4 space-y-4">
            {microsoftStats.map((stat) => (
              <div key={stat.name} className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="font-medium">{stat.name}</span>
                  <span className="text-muted-foreground">
                    {stat.percentage.toFixed(1)}%
                  </span>
                </div>
                <div className="relative h-2 dark:bg-input bg-muted rounded-full overflow-hidden">
                  <div
                    className={`absolute top-0 left-0 h-full rounded-full ${
                      stat.name === "Junk" ? "bg-red-500" : "bg-primary"
                    }`}
                    style={{ width: `${stat.percentage}%` }}
                  />
                </div>
                <div className="text-right">
                  <span className="text-sm text-muted-foreground">
                    Count: {stat.score.toLocaleString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
        )}

       

        {/* Gmail Email Placement */}
        {(!sender || selectedSeeds.includes("personal-gmail")) && (
        <div className="space-y-2">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            Email Placement - Personal Gmail
          </h2>

          <div className="bg-background border rounded-lg p-4 space-y-4">
            {gmailStats.map((stat) => (
              <div key={stat.name} className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="font-medium">{stat.name}</span>
                  <span className="text-muted-foreground">
                    {stat.percentage.toFixed(1)}%
                  </span>
                </div>
                <div className="relative h-2 dark:bg-input bg-muted rounded-full overflow-hidden">
                  <div
                    className={`absolute top-0 left-0 h-full rounded-full ${
                      stat.name === "Spam" ? "bg-red-500" : "bg-primary"
                    }`}
                    style={{ width: `${stat.percentage}%` }}
                  />
                </div>
                <div className="text-right">
                  <span className="text-sm text-muted-foreground">
                    Count: {stat.score.toLocaleString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
        )}

       

        {/* Microsoft 365 Email Placement */}
        {(!sender || selectedSeeds.includes("personal-outlook")) && (
        <div className="space-y-2">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            Email Placement - Personal Outlook
          </h2>

          <div className="bg-background border rounded-lg p-4 space-y-4">
            {outlookStats.map((stat) => (
              <div key={stat.name} className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="font-medium">{stat.name}</span>
                  <span className="text-muted-foreground">
                    {stat.percentage.toFixed(1)}%
                  </span>
                </div>
                <div className="relative h-2 dark:bg-input bg-muted rounded-full overflow-hidden">
                  <div
                    className={`absolute top-0 left-0 h-full rounded-full ${
                      stat.name === "Junk" ? "bg-red-500" : "bg-primary"
                    }`}
                    style={{ width: `${stat.percentage}%` }}
                  />
                </div>
                <div className="text-right">
                  <span className="text-sm text-muted-foreground">
                    Count: {stat.score.toLocaleString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
        )}

{(!sender || selectedSeeds.includes("personal-yahoo")) && (
        <div className="space-y-2">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            Email Placement - Personal Yahoo
          </h2>

          <div className="bg-background border rounded-lg p-4 space-y-4">
            {yahooStats.map((stat) => (
              <div key={stat.name} className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="font-medium">{stat.name}</span>
                  <span className="text-muted-foreground">
                    {stat.percentage.toFixed(1)}%
                  </span>
                </div>
                <div className="relative h-2 dark:bg-input bg-muted rounded-full overflow-hidden">
                  <div
                    className={`absolute top-0 left-0 h-full rounded-full ${
                      stat.name === "Spam" ? "bg-red-500" : "bg-primary"
                    }`}
                    style={{ width: `${stat.percentage}%` }}
                  />
                </div>
                <div className="text-right">
                  <span className="text-sm text-muted-foreground">
                    Count: {stat.score.toLocaleString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
        )}

{(!sender || selectedSeeds.includes("personal-zoho")) && (
        <div className="space-y-2">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            Email Placement - Personal Zoho
          </h2>

          <div className="bg-background border rounded-lg p-4 space-y-4">
            {zohoStats.map((stat) => (
              <div key={stat.name} className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="font-medium">{stat.name}</span>
                  <span className="text-muted-foreground">
                    {stat.percentage.toFixed(1)}%
                  </span>
                </div>
                <div className="relative h-2 dark:bg-input bg-muted rounded-full overflow-hidden">
                  <div
                    className={`absolute top-0 left-0 h-full rounded-full ${
                      stat.name === "Spam" ? "bg-red-500" : "bg-primary"
                    }`}
                    style={{ width: `${stat.percentage}%` }}
                  />
                </div>
                <div className="text-right">
                  <span className="text-sm text-muted-foreground">
                    Count: {stat.score.toLocaleString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
        )}

       

      

        <div className="mt-4 md:mt-8">
          <div className="justify-between w-full items-center flex mb-2">
            <h2 className="text-xl font-semibold">Daily Statistics</h2>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={exportToCSV} disabled={isExporting}>
                {isExporting ? (
                  <span className="h-4 w-4 mr-2 inline-block animate-spin rounded-full border-2 border-current border-r-transparent" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                {isExporting ? "Exporting..." : "Export"}
              </Button>
            </div>
          </div>
          <DataTable
            ref={gridRef}
            rowData={combinedTableData}
            columnDefs={columns}
            height={400}
            // loading={isSenderInteractionsLoading}
            defaultColDef={{
              sortable: true,
              resizable: true,
              filter: true,
            }}
          />
        </div>

        {/* Summary Stats */}
      </div>

      {/* Sender IPs Dialog */}
      <Dialog 
        open={showSenderIpsDialog} 
        onOpenChange={(open) => {
          setShowSenderIpsDialog(open);
          if (!open) {
            // Reset UI state when dialog closes
            setSelectedSenderIps([]);
            setSelectedSenderIpHostnames({});
            setSelectedIpCounts({});
          }
        }}
      >
        <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex flex-col gap-1">
              <span>Sender Profile IPs</span>
              <span className="text-xs font-normal text-muted-foreground">
                {selectedSenderIps.length} total IP
                {selectedSenderIps.length === 1 ? "" : "s"} •{" "}
                {totalSenderIpEmails.toLocaleString()} emails
                {selectedDate
                  ? ` • ${new Date(selectedDate).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}`
                  : ""}
              </span>
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4 flex-1 overflow-hidden">
            {selectedSenderIps.length > 0 ? (
              <div className="border rounded-lg overflow-hidden">
                {/* Table header */}
                <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/40">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary text-xs">
                      👤
                    </span>
                    <span>Sender Profile IPs</span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {selectedSenderIps.length} total IP
                    {selectedSenderIps.length === 1 ? "" : "s"} •{" "}
                    {totalSenderIpEmails.toLocaleString()} emails
                  </div>
                </div>

                {/* Column headings */}
                <div className="grid grid-cols-[2fr,3fr,1.5fr] px-4 py-2 text-xs font-medium text-muted-foreground border-b bg-muted/30">
                  <span>IP ADDRESS</span>
                  <span>HOSTNAME</span>
                  <span className="text-right">NO. OF EMAILS</span>
                </div>

                {/* Rows */}
                <div className="max-h-[50vh] overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-muted [&::-webkit-scrollbar-thumb]:bg-muted-foreground/30 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-muted-foreground/50">
                  {selectedSenderIps.map((ip, index) => {
                    const hostnameInfo =
                      selectedSenderIpHostnames && selectedSenderIpHostnames[ip];
                    const hostname = hostnameInfo?.hostname ?? null;
                    const sanitizedKey = ip.replace(/[.:]/g, "_");
                    const count =
                      (selectedIpCounts && selectedIpCounts[ip]) ??
                      (selectedIpCounts && selectedIpCounts[sanitizedKey]) ??
                      0;

                    return (
                      <div
                        key={index}
                        className="grid grid-cols-[2fr,3fr,1.5fr] items-center px-4 py-3 text-sm border-b last:border-b-0"
                      >
                        {/* IP */}
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs sm:text-sm">
                            {ip}
                          </span>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(ip);
                              toast.success("IP copied to clipboard");
                            }}
                            className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
                            title="Copy IP"
                            type="button"
                          >
                            Copy
                          </button>
                        </div>

                        {/* Hostname */}
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-xs sm:text-sm truncate">
                            {hostname || (
                              <span className="italic text-muted-foreground">
                                No hostname available
                              </span>
                            )}
                          </span>
                          {hostname && (
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(hostname);
                                toast.success("Hostname copied to clipboard");
                              }}
                              className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline whitespace-nowrap"
                              title="Copy hostname"
                              type="button"
                            >
                              Copy
                            </button>
                          )}
                        </div>

                        {/* Count */}
                        <div className="text-right text-xs sm:text-sm font-medium">
                          {typeof count === "number"
                            ? count.toLocaleString()
                            : "0"}
                        </div>
                      </div>
                    );
                  })}

                  <div className="text-center py-3 text-xs text-muted-foreground">
                    Showing all {selectedSenderIps.length} sender profile IP
                    {selectedSenderIps.length === 1 ? "" : "s"}
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8">
                No sender IPs available
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Template Dialog */}
      <Dialog
        open={showEmailTemplateDialog}
        onOpenChange={(open) => {
          setShowEmailTemplateDialog(open);
          if (!open) {
            setSelectedTemplates([]);
            setActiveTemplateIndex(0);
            setParsedEmail(null);
            setIsParsingEmail(false);
            setSelectedEmailTemplate("");
            setSelectedTemplateForPreview(null);
            setExpandedEsps(new Set());
            setIsReceivingChainOpen(true);
          }
        }}
      >
        <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <div className="flex items-center gap-3">
              {selectedTemplateForPreview && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleBackFromTemplatePreview}
                  className="h-8 w-8"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              )}
              <DialogTitle>
                {selectedTemplateForPreview ? "Template Preview" : "Email Templates"}
                {selectedTemplates && selectedTemplates.length > 1
                  ? ` (${selectedTemplates.length})`
                  : ""}
              </DialogTitle>
            </div>
          </DialogHeader>
          <div className="mt-4 flex-1 overflow-hidden">
            {selectedTemplates && selectedTemplates.length > 0 ? (
              selectedTemplateForPreview ? (
                // Template Preview View (dashboard-style)
                <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-muted [&::-webkit-scrollbar-thumb]:bg-muted-foreground/30 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-muted-foreground/50">
                  {isParsingEmail ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin mr-2" />
                      <span className="text-sm text-muted-foreground">
                        Parsing email...
                      </span>
                    </div>
                  ) : parsedEmail ? (
                    <>
                      {/* Email Metadata */}
                      <div className="p-4 bg-muted rounded-lg border">
                        <div className="text-sm font-semibold mb-4">
                          Email Information
                        </div>
                        <div className="space-y-4">
                          {/* To */}
                          <div className="flex items-start gap-3">
                            <Mail className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="text-xs text-muted-foreground mb-1">
                                To:
                              </div>
                              <div className="font-medium break-words">
                                {parsedEmail.to?.address || "-"}
                              </div>
                            </div>
                          </div>

                          {/* From */}
                          <div className="flex items-start gap-3">
                            <Users2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="text-xs text-muted-foreground mb-1">
                                From:
                              </div>
                              <div className="font-medium break-words">
                                {parsedEmail.from?.address ||
                                  (selectedTemplateForPreview as any)
                                    ?.sender_email ||
                                  mailbox?.email ||
                                  "-"}
                              </div>
                            </div>
                          </div>

                          {/* IP */}
                          <div className="flex items-start gap-3">
                            <Network className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="text-xs text-muted-foreground mb-1">
                                IP:
                              </div>
                              <div className="font-medium font-mono text-sm break-all">
                                {parsedEmail.senderIP || "-"}
                              </div>
                            </div>
                          </div>

                          {/* Subject */}
                          <div className="flex items-start gap-3">
                            <MailIcon className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="text-xs text-muted-foreground mb-1">
                                Subject:
                              </div>
                              <div className="font-medium break-words whitespace-normal">
                                {parsedEmail.subject || "No Subject"}
                              </div>
                            </div>
                          </div>

                          {/* Date */}
                          <div className="flex items-start gap-3">
                            <Calendar className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="text-xs text-muted-foreground mb-1">
                                Date:
                              </div>
                              <div className="font-medium">
                                {parsedEmail.date
                                  ? parsedEmail.date.toLocaleString("en-US", {
                                      year: "numeric",
                                      month: "2-digit",
                                      day: "2-digit",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                      second: "2-digit",
                                    })
                                  : "-"}
                              </div>
                            </div>
                          </div>

                          {/* Message ID */}
                          <div className="flex items-start gap-3">
                            <Hash className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="text-xs text-muted-foreground mb-1">
                                Message ID:
                              </div>
                              <div className="font-medium font-mono text-sm break-all">
                                {parsedEmail.messageId || "-"}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Raw Email Headers Section */}
                      {selectedTemplateForPreview &&
                        (selectedTemplateForPreview as any).raw_mail && (
                          <div className="p-4 bg-muted rounded-lg border">
                            <div className="flex items-center justify-between mb-3">
                              <div className="text-sm font-semibold">
                                Raw Email Headers
                              </div>
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    const raw =
                                      (selectedTemplateForPreview as any)
                                        .raw_mail || "";
                                    const headersText =
                                      raw.split("\r\n\r\n")[0] ||
                                      raw.split("\n\n")[0] ||
                                      raw;
                                    navigator.clipboard.writeText(
                                      headersText ?? ""
                                    );
                                    toast.success("Headers copied to clipboard");
                                  }}
                                >
                                  <Copy className="h-4 w-4 mr-2" />
                                  Copy
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    const raw =
                                      (selectedTemplateForPreview as any)
                                        .raw_mail || "";
                                    const blob = new Blob([raw], {
                                      type: "text/plain",
                                    });
                                    const url =
                                      URL.createObjectURL(blob);
                                    const a =
                                      document.createElement("a");
                                    a.href = url;
                                    a.download = "headers-email-template.txt";
                                    a.click();
                                    URL.revokeObjectURL(url);
                                  }}
                                >
                                  <Download className="h-4 w-4 mr-2" />
                                  Download
                                </Button>
                              </div>
                            </div>
                            <div className="p-3 bg-background rounded border font-mono text-xs text-muted-foreground max-h-[300px] overflow-y-auto overflow-x-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-muted [&::-webkit-scrollbar-thumb]:bg-muted-foreground/30 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-muted-foreground/50">
                              <pre className="whitespace-pre-wrap break-words">
                                {(selectedTemplateForPreview as any)
                                  .raw_mail.split("\r\n\r\n")[0] ||
                                  (selectedTemplateForPreview as any)
                                    .raw_mail.split("\n\n")[0] ||
                                  (selectedTemplateForPreview as any).raw_mail}
                              </pre>
                            </div>
                          </div>
                        )}

                      {/* Receiving Chain Section */}
                      {parsedEmail.receivingChain &&
                        parsedEmail.receivingChain.length > 0 && (
                          <Collapsible
                            open={isReceivingChainOpen}
                            onOpenChange={setIsReceivingChainOpen}
                          >
                            <div className="p-4 bg-muted rounded-lg border">
                              <CollapsibleTrigger className="w-full flex items-center justify-between hover:bg-muted/50 transition-colors rounded p-2 -m-2">
                                <div className="flex items-center gap-2">
                                  <Globe className="h-4 w-4" />
                                  <div className="text-sm font-semibold">
                                    Email Delivery Chain
                                  </div>
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>More Info</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                </div>
                                {isReceivingChainOpen ? (
                                  <ChevronUp className="h-4 w-4 text-muted-foreground" />
                                ) : (
                                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                )}
                              </CollapsibleTrigger>
                              <CollapsibleContent>
                                <div className="mt-3">
                                  <div className="text-xs text-muted-foreground mb-3 flex items-center gap-2">
                                    <Globe className="h-3 w-3" />
                                    <span>Receiving Chain</span>
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          <p>
                                            Step-by-step routing and processing
                                            time of your email across SMTP
                                            servers.
                                          </p>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  </div>
                                  <div className="border rounded-lg overflow-hidden">
                                    <Table>
                                      <TableHeader>
                                        <TableRow>
                                          <TableHead className="w-12">
                                            #
                                          </TableHead>
                                          <TableHead>Delay</TableHead>
                                          <TableHead>From</TableHead>
                                          <TableHead className="w-4"></TableHead>
                                          <TableHead>To</TableHead>
                                          <TableHead>Protocol</TableHead>
                                          <TableHead>Time received</TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {parsedEmail.receivingChain.map(
                                          (item) => (
                                            <TableRow key={item.index}>
                                              <TableCell className="font-medium">
                                                {item.index}
                                              </TableCell>
                                              <TableCell>
                                                <span className="text-green-600 dark:text-green-400">
                                                  {item.delay}
                                                </span>
                                              </TableCell>
                                              <TableCell className="font-mono text-xs break-all">
                                                {item.from}
                                              </TableCell>
                                              <TableCell>
                                                <ArrowRight className="h-4 w-4 text-muted-foreground" />
                                              </TableCell>
                                              <TableCell className="font-mono text-xs break-all">
                                                {item.to}
                                              </TableCell>
                                              <TableCell>
                                                {item.protocol}
                                              </TableCell>
                                              <TableCell className="text-xs">
                                                {item.timeReceived}
                                              </TableCell>
                                            </TableRow>
                                          )
                                        )}
                                      </TableBody>
                                    </Table>
                                  </div>
                                </div>
                              </CollapsibleContent>
                            </div>
                          </Collapsible>
                        )}

                      {/* Email Content with Tabs */}
                      <div className="space-y-2">
                        <div className="text-sm font-semibold">
                          Email Content
                        </div>
                        <Tabs defaultValue="rendered" className="w-full">
                          <TabsList className="grid w-full grid-cols-2">
                            <TabsTrigger value="plain-text">
                              Plain Text
                            </TabsTrigger>
                            <TabsTrigger value="rendered">
                              Rendered
                            </TabsTrigger>
                          </TabsList>
                          <TabsContent value="plain-text" className="mt-4">
                            <div className="p-4 bg-background rounded-lg border min-h-[300px]">
                              <div className="text-sm whitespace-pre-wrap break-words">
                                {parsedEmail.text ||
                                  (parsedEmail.html
                                    ? parsedEmail.html
                                        .replace(/<[^>]*>/g, "")
                                        .replace(/\s+/g, " ")
                                        .trim()
                                    : "")}
                              </div>
                            </div>
                          </TabsContent>
                          <TabsContent value="rendered" className="mt-4">
                            <div className="border rounded-lg overflow-hidden bg-white">
                              <iframe
                                srcDoc={
                                  parsedEmail.html ||
                                  parsedEmail.text ||
                                  ""
                                }
                                sandbox="allow-same-origin"
                                className="w-full h-[500px] border-0"
                                title={`Email Template - ${
                                  parsedEmail.subject || "No Subject"
                                }`}
                              />
                            </div>
                          </TabsContent>
                        </Tabs>
                      </div>
                    </>
                  ) : null}
                </div>
              ) : (
                // Sender + ESP selection view
                <div className="max-h-[70vh] overflow-y-auto pr-2 space-y-4 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-muted [&::-webkit-scrollbar-thumb]:bg-muted-foreground/30 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-muted-foreground/50">
                  {(() => {
                    const senderEmail =
                      (selectedTemplates[0] as any)?.sender_email ||
                      mailbox?.email ||
                      "";
                    const templateCount = selectedTemplates.length;
                    const espKeys = Object.keys(groupedTemplatesByEsp);
                    const espCount = espKeys.length;

                    return (
                      <>
                        {/* Sender Card */}
                        <div className="border rounded-lg bg-background p-4 flex items-center gap-3">
                          <div className="bg-primary/10 p-2 rounded-full">
                            <Users2 className="h-6 w-6 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-sm truncate">
                              {senderEmail}
                            </div>
                            <div className="text-xs text-muted-foreground mt-1 flex items-center gap-2">
                              <span>
                                {templateCount} template
                                {templateCount !== 1 ? "s" : ""} • {espCount}{" "}
                                ESP
                                {espCount !== 1 ? "s" : ""}
                              </span>
                              {espKeys.length > 0 && (
                                <>
                                  <span>•</span>
                                  <div className="flex items-center gap-1.5">
                                    {espKeys.map((esp) => {
                                      const espTypeStr = String(esp);
                                      const iconMapping =
                                        authProviderIcons[espTypeStr] ||
                                        authProviderIcons[
                                          espTypeStr.toLowerCase()
                                        ] ||
                                        authProviderIcons["smtp"];
                                      return (
                                        <TooltipProvider key={esp}>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <div className="cursor-help">
                                                {iconMapping?.icon || (
                                                  <Mail className="h-4 w-4" />
                                                )}
                                              </div>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                              <p>
                                                {esp.replace(/_/g, " ")}
                                              </p>
                                            </TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>
                                      );
                                    })}
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* ESP Sections */}
                        {espKeys.length > 0 ? (
                          espKeys.map((esp) => {
                            const espTemplates = groupedTemplatesByEsp[esp];
                            const isExpanded = expandedEsps.has(esp);
                            return (
                              <div
                                key={esp}
                                className="border rounded-lg overflow-hidden bg-muted/30"
                              >
                                {/* ESP Header */}
                                <div
                                  onClick={() => {
                                    if (espTemplates.length === 1) {
                                      handleTemplateSelect(espTemplates[0]);
                                    } else {
                                      toggleEsp(esp);
                                    }
                                  }}
                                  className="p-4 hover:bg-muted/40 transition-colors cursor-pointer flex items-center justify-between"
                                >
                                  <div className="flex items-center gap-2">
                                    {(() => {
                                      const espTypeStr = String(esp);
                                      const iconMapping =
                                        authProviderIcons[espTypeStr] ||
                                        authProviderIcons[
                                          espTypeStr.toLowerCase()
                                        ] ||
                                        authProviderIcons["smtp"];
                                      return (
                                        <TooltipProvider>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <div className="flex items-center gap-2">
                                                {iconMapping?.icon || (
                                                  <Mail className="h-4 w-4" />
                                                )}
                                              </div>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                              <p>
                                                {esp.replace(/_/g, " ")}
                                              </p>
                                            </TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>
                                      );
                                    })()}
                                    <Badge
                                      variant="outline"
                                      className="uppercase text-xs"
                                    >
                                      {esp.replace(/_/g, " ")}
                                    </Badge>
                                  </div>
                                  {espTemplates.length > 1 && (
                                    <>
                                      {isExpanded ? (
                                        <ChevronUp className="h-4 w-4 text-muted-foreground" />
                                      ) : (
                                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                      )}
                                    </>
                                  )}
                                </div>

                                {/* Templates List for ESP */}
                                {espTemplates.length > 1 && isExpanded && (
                                  <div className="bg-background">
                                    {espTemplates.map((template, idx) => {
                                      const raw: string =
                                        (template as any).raw_mail || "";
                                      const subjectMatch = raw
                                        ? raw.match(
                                            /Subject:\s*(.+?)(?:\r?\n|$)/i
                                          )
                                        : null;
                                      const subject =
                                        subjectMatch?.[1]?.trim() ||
                                        (template as any).subject ||
                                        `Template ${idx + 1}`;

                                      // Extract a small plain-text snippet
                                      let snippet = "";
                                      if (raw) {
                                        const headerEnd =
                                          raw.indexOf("\r\n\r\n") !== -1
                                            ? raw.indexOf("\r\n\r\n")
                                            : raw.indexOf("\n\n");
                                        const body =
                                          headerEnd !== -1
                                            ? raw.substring(headerEnd + 2)
                                            : raw;
                                        const textBody = body
                                          .replace(/<[^>]*>/g, "")
                                          .replace(/\s+/g, " ")
                                          .trim();
                                        snippet =
                                          textBody.substring(0, 200) +
                                          (textBody.length > 200
                                            ? "..."
                                            : "");
                                      }

                                      return (
                                        <div
                                          key={idx}
                                          className="p-4 space-y-3 border-t last:border-b-0 cursor-pointer hover:bg-muted/40 transition-colors"
                                          onClick={() =>
                                            handleTemplateSelect(template)
                                          }
                                        >
                                          <div className="flex items-center gap-2">
                                            <span className="text-xs text-muted-foreground uppercase">
                                              SUBJECT :
                                            </span>
                                            <div className="font-semibold text-sm">
                                              {subject}
                                            </div>
                                          </div>

                                          <div className="flex items-center gap-2">
                                            <span className="text-xs text-muted-foreground uppercase">
                                              Template Body
                                            </span>
                                            <TooltipProvider>
                                              <Tooltip>
                                                <TooltipTrigger asChild>
                                                  <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                  <p>
                                                    Click to open the full
                                                    template preview
                                                  </p>
                                                </TooltipContent>
                                              </Tooltip>
                                            </TooltipProvider>
                                          </div>

                                          <div className="p-3 bg-muted rounded border text-xs text-muted-foreground line-clamp-3">
                                            {snippet || "No content"}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })
                        ) : (
                          <p className="text-muted-foreground text-center py-8 text-sm">
                            No templates found
                          </p>
                        )}
                      </>
                    );
                  })()}
                </div>
              )
            ) : selectedEmailTemplate ? (
              <div className="p-4 bg-background rounded-lg border prose prose-sm dark:prose-invert max-w-none">
                <div
                  dangerouslySetInnerHTML={{ __html: selectedEmailTemplate }}
                />
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8">
                No email template available
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
